import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { useChat } from '../hooks/useChat';
import { apiGetAssistantBusinessContext, apiGetCeoDashboard, apiGetStoreManagerDashboard, apiTrackObservabilityEvent } from '../utils/authApi';
import { X, Send, Bot, User, Minimize2, Maximize2, Loader2, Sparkles, Trash2 } from 'lucide-react';
import posIntentCatalog from '../config/assistant-pos-intents.json';

const EXCLUDED_ROUTES = [];

const SYSTEM_MESSAGE = {
  role: 'system',
  content: `Você é um assistente de IA integrado ao ERP Dissa Ax.
Antes de responder, analise internamente as regras e a lógica de negócio do sistema.
Entenda o funcionamento do ERP, o escopo do utilizador, os fluxos fiscais e operacionais, e somente depois ofereça uma resposta.
Responda sempre em Português (Angola), com estilo objetivo, humano e orientado a procedimentos.

Formato padrão da resposta:
1) Objetivo
2) Passos práticos
3) Verificação do resultado
4) Próxima ação

Antes de concluir qualquer orientação de operação, valide pré-requisitos necessários em marcadores.
Se faltar informação para executar o procedimento com segurança, faça perguntas objetivas e específicas.

Regras de confirmação:
- Peça confirmação apenas quando for estritamente necessário (ação irreversível, ambiguidade real, falta de permissão/dado crítico).
- Quando precisar confirmar, faça apenas uma pergunta curta e direta.
- Não peça confirmações redundantes.

Regras de conteúdo:
- Analise sempre primeiro a lógica de negócio e as regras do ERP antes de sugerir um procedimento.
- Para perguntas analíticas, avalie os dados do sistema, a lógica dos KPIs e o contexto financeiro antes de responder.
- Nunca invente dados de negócio, clientes, produtos ou valores.
- Se faltar dado real, diga a limitação e indique exatamente onde obter no sistema.
- Priorize instruções executáveis no contexto da tela/módulo atual.
- Responda de forma humanizada, clara e prática; explique o porquê quando necessário.
- Mantenha respostas curtas por padrão; detalhe apenas se o utilizador pedir.

Exemplo few-shot:
Pergunta: "Como emitir segunda via do documento?"
Resposta esperada: "Posso orientar no fluxo exato para FT, FR, PP, OR, retificação ou cancelamento. Antes de finalizar, confirme estes pontos rápidos:\n- Caixa aberta no POS e sessão válida (empresa/loja/utilizador).\n- Série ativa para o documento selecionado.\n- Itens válidos no carrinho e regras fiscais respeitadas.\nApós a confirmação, indique o tipo de documento para eu fornecer o procedimento exato."
`,
};

const QUICK_SUGGESTIONS = [
  'Como adicionar um fornecedor?',
  'Ver relatório de tesouraria',
  'Como registar uma factura?',
  'Ajuda com folha salarial',
];

const INITIAL_ASSISTANT_GREETING = 'Olá! Sou o assistente IA do Dissa Ax. Como posso ajudá-lo hoje? 👋';

const CHAT_HISTORY_STORAGE_PREFIX = 'angolabi.erp.chatbot.history.v1.';
const CHAT_TELEMETRY_LOCAL_KEY = 'angolabi.erp.chatbot.telemetry.local.v1';
const CHAT_TELEMETRY_QUEUE_KEY = 'angolabi.erp.chatbot.telemetry.queue.v1';
const CHAT_HISTORY_LIMIT = 120;
const CHAT_TELEMETRY_LIMIT = 300;
const ERP_AUTH_EVENT = 'angolabi:erp-auth';
const BUSINESS_CONTEXT_CACHE_TTL_MS = 0;
const INVALID_BUSINESS_CONTEXT_TOKENS = new Set(['', '__cookie_session__', 'offline-session']);
const OUT_OF_SCOPE_POLICY_MESSAGE = 'Nao posso aceder nem partilhar dados de outras empresas. Posso ajudar apenas com dados do seu escopo autorizado (empresa/loja da sua sessao).';
const CONTEXT_INTENT_MAP_VERSION = '2026-05-27.4';

const PROFORMA_DETERMINISTIC_ANSWER = 'No DISSA AX, a Proforma e emitida como documento PP. Fluxo correto: no POS, selecione tipo de documento PP, escolha o cliente (PP exige cliente), adicione itens (PP nao permite itens de servico), confirme a serie e finalize. Observacoes importantes: PP nao tem pagamento imediato, e em plano Trial/licenca inativa a emissao de PP pode ficar bloqueada.';
const FISCAL_RULE_FIRST_BASE = [
  'Caixa aberta no POS e sessão válida (empresa/loja/utilizador).',
  'Série ativa para o documento selecionado.',
  'Itens válidos no carrinho e regras fiscais respeitadas.',
];

function buildFiscalRuleBlock() {
  const bullets = FISCAL_RULE_FIRST_BASE.map((rule) => `- ${rule}`).join('\n');
  return `Antes de finalizar, confirme estes pontos rápidos:\n${bullets}`;
}

function isFiscalRuleFirstQuestion(text) {
  const normalized = normalizeLabelKey(text);
  if (!normalized) return false;

  return /emitir|emissao|fatura|factura|ft|fr|proforma|pro forma|\bpp\b|orcamento|\bor\b|retificar|retificacao|cancelar|cancelamento/.test(normalized);
}

function buildFiscalRuleFirstAnswer(text) {
  const normalized = normalizeLabelKey(text);
  if (!normalized) return null;
  if (!isFiscalRuleFirstQuestion(text)) return null;

  const ruleBlock = buildFiscalRuleBlock();

  if (/segunda via|2 via|segunda-via|reimpressao|reimpressa|reemitir|emitir novamente/.test(normalized)) {
    return `Posso orientar no fluxo exato para FT, FR, PP, OR, retificação ou cancelamento.\n\n${ruleBlock}\n\nApós a confirmação, indique o tipo de documento para eu fornecer o procedimento exato de segunda via.`;
  }

  if (/proforma|pro forma|\bpp\b/.test(normalized)) {
    return `Claro. Para registar uma Proforma (PP), faça assim no POS:\n1. Selecione o documento PP.\n2. Escolha o cliente (obrigatório para PP).\n3. Adicione os itens (PP não permite item de serviço).\n4. Confirme a série e grave.\n\n${ruleBlock}\n\nNota: PP não exige pagamento imediato e pode ficar bloqueada em plano Trial/licença inativa.`;
  }

  if (/\bft\b|fatura|factura/.test(normalized)) {
    return `Perfeito. Para registar uma Fatura (FT) no POS:\n1. Abra a venda e selecione o documento FT.\n2. Confirme o cliente (ou Consumidor Final, conforme a regra da sua configuração).\n3. Adicione os itens e confira preços/quantidades.\n4. Valide a série ativa, confirme o pagamento e finalize.\n\n${ruleBlock}\n\nSe aparecer bloqueio, normalmente é por caixa fechada, série inativa ou divergência de escopo/documento.`;
  }

  if (/\bfr\b/.test(normalized)) {
    return `Para emitir FR no POS:\n1. Selecione FR na venda atual.\n2. Confirme a série ativa.\n3. Valide o cliente (Consumidor Final conforme configuração).\n4. Adicione os itens e finalize.\n\n${ruleBlock}\n\nSe a venda estiver divergente no backend, re-sincronize antes de concluir.`;
  }

  if (/\bor\b|orcamento/.test(normalized)) {
    return `Para emitir Orçamento (OR):\n1. Selecione OR no POS.\n2. Confirme a série ativa.\n3. Valide itens/valores e finalize no escopo da sua sessão.\n\n${ruleBlock}\n\nO OR segue validações de consistência e pode ter regras de pagamento diferentes de FR.`;
  }

  if (/retificar|retificacao/.test(normalized)) {
    return `Para retificar um documento:\n1. Abra Transações.\n2. Localize o documento correto.\n3. Confirme elegibilidade fiscal e estado atual.\n4. Execute a retificação e valide o retorno final.`;
  }

  if (/cancelar|cancelamento/.test(normalized)) {
    return `Para cancelar um documento:\n1. Abra Transações.\n2. Selecione um documento elegível.\n3. Informe o motivo e confirme.\n4. Valide o estado final e a sincronização.`;
  }

  return `Posso orientar no fluxo exato para FT, FR, PP, OR, retificação ou cancelamento.\n\n${ruleBlock}`;
}

const POS_PRIORITY_INTENT_RULES = [
  { id: 'pos_q_001', priority: 200, pattern: /caixa.*aberta|abertura.*caixa|caixa.*fechada/, answer: 'Estado da caixa no POS: confirme no bloco de abertura/fecho da tela. Se estiver fechada, abra a caixa antes de vender.' },
  { id: 'pos_q_002', priority: 199, pattern: /quem.*abriu|quem.*fechou|responsavel.*caixa/, answer: 'Responsavel da caixa: veja o registo de abertura/fecho do dia no POS, onde aparece o utilizador associado ao movimento.' },
  { id: 'pos_q_003', priority: 198, pattern: /saldo inicial|valor.*abertura/, answer: 'Saldo inicial: consulte o registo de abertura do dia no POS. Esse valor e a base para controlo de caixa.' },
  { id: 'pos_q_004', priority: 197, pattern: /fecho.*hoje|ja.*fechou.*hoje|relatorio diario.*hoje/, answer: 'Fecho diario de hoje: valide no modulo de Relatorio Diario. Se ja estiver fechado, novas vendas podem ficar bloqueadas no periodo.' },
  { id: 'pos_q_005', priority: 196, pattern: /valor fisico|numerario fisico|caixa fisico/, answer: 'Valor fisico do fecho: informe e confira no Relatorio Diario antes de concluir o fecho.' },
  { id: 'pos_q_006', priority: 195, pattern: /diferenca.*fecho|quebra.*caixa|sobra.*caixa/, answer: 'Diferenca de fecho: compare valor esperado e valor fisico no Relatorio Diario para identificar quebra ou sobra.' },
  { id: 'pos_q_007', priority: 194, pattern: /abrir.*caixa.*novo|reabrir.*caixa|abrir.*mesmo.*dia/, answer: 'Reabertura de caixa: o POS bloqueia nova abertura no mesmo dia quando ja houve fecho. Verifique o registo diario.' },
  { id: 'pos_q_008', priority: 193, pattern: /vender.*caixa.*fechada|caixa fechada.*venda/, answer: 'Venda com caixa fechada: nao permitido. Abra a caixa primeiro para continuar operacoes no POS.' },
  { id: 'pos_q_009', priority: 192, pattern: /fecho.*offline|fecho.*online|fecho.*sincron/, answer: 'Fecho online/offline: quando sem rede, o fecho pode ficar pendente de sincronizacao na fila offline do POS.' },
  { id: 'pos_q_010', priority: 191, pattern: /abertura.*offline|abertura.*online|saldo inicial.*offline/, answer: 'Abertura online/offline: sem rede, a abertura pode ser guardada localmente e sincronizada depois.' },
  { id: 'pos_q_011', priority: 190, pattern: /mensagem.*caixa fechada|ja fechou a caixa/, answer: 'Mensagem de caixa fechada: indica que ja houve fecho no dia e bloqueia nova abertura/vendas ate o proximo ciclo.' },
  { id: 'pos_q_012', priority: 189, pattern: /timeout.*carga|tempo.*carregar.*pos|loading.*timeout/, answer: 'Timeout de carga: o POS usa protecao de tempo de carregamento. Se exceder, recarregue e valide sessao/rede.' },
  { id: 'pos_q_013', priority: 188, pattern: /watchdog.*loading|loading guard|travou.*carregar/, answer: 'Watchdog de loading: quando detecta travamento, force refresh da tela e valide os servicos de API.' },
  { id: 'pos_q_014', priority: 187, pattern: /verificacao silenciosa|checagem silenciosa|cash check/, answer: 'Verificacao silenciosa de caixa: roda periodicamente para manter o estado de abertura/fecho atualizado.' },
  { id: 'pos_q_015', priority: 186, pattern: /observacao.*fecho|nota.*fecho|comentario.*fecho/, answer: 'Observacao de fecho: pode ser incluida no Relatorio Diario para auditoria e rastreabilidade.' },
  { id: 'pos_q_016', priority: 185, pattern: /data.*fecho|dia.*fecho|fecho.*data/, answer: 'Data de fecho: o POS referencia o dia operacional atual no relatorio diario do utilizador/loja.' },
  { id: 'pos_q_017', priority: 184, pattern: /venda.*sem abertura|abrir caixa.*antes.*vender/, answer: 'Venda sem abertura valida: bloqueada por regra operacional. Registe saldo inicial antes de vender.' },
  { id: 'pos_q_018', priority: 183, pattern: /revalidar.*caixa|validar.*caixa.*antes.*finalizar/, answer: 'Antes de finalizar venda, o POS revalida estado da caixa para evitar conclusao fora do ciclo operacional.' },
  { id: 'pos_q_019', priority: 182, pattern: /sessao.*caixa|token.*caixa|sessao invalida.*caixa/, answer: 'Sessao de caixa invalida: refaca login e tente novamente para abertura/fecho.' },
  { id: 'pos_q_020', priority: 181, pattern: /cache.*fecho|fecho.*cache local|registro local.*fecho/, answer: 'Cache local de fecho: o POS pode manter snapshot local por escopo (empresa/loja/utilizador/dia).' },
  { id: 'pos_q_021', priority: 180, pattern: /quantas.*despesas|despesas.*periodo|total.*itens.*despesa/, answer: 'Despesas no periodo: use os filtros de data e pesquisa no modal de despesas para ver a contagem atual.' },
  { id: 'pos_q_022', priority: 179, pattern: /total.*despesas|soma.*despesas|valor.*despesas/, answer: 'Total de despesas: e calculado pelo resumo do modal de despesas no periodo filtrado.' },
  { id: 'pos_q_023', priority: 178, pattern: /despesa.*recente|ultima despesa|mais recente.*despesa/, answer: 'Despesa mais recente: aparece no topo da lista quando o registo e salvo com sucesso.' },
  { id: 'pos_q_024', priority: 177, pattern: /despesa.*sem nome|nome.*despesa.*obrigatorio/, answer: 'Despesa sem nome: bloqueada. O POS exige nome da despesa antes de gravar.' },
  { id: 'pos_q_025', priority: 176, pattern: /despesa.*sem motivo|motivo.*despesa.*obrigatorio/, answer: 'Despesa sem motivo: bloqueada. Informe o motivo da saida para continuar.' },
  { id: 'pos_q_026', priority: 175, pattern: /despesa.*valor 0|despesa.*valor invalido|saida.*valor invalido/, answer: 'Valor da despesa deve ser positivo e valido. O POS rejeita valor zero ou invalido.' },
  { id: 'pos_q_027', priority: 174, pattern: /validar.*despesa.*caixa|sem numerario.*despesa|validacao.*pos expense/, answer: 'A despesa e validada contra numerario disponivel antes de gravar.' },
  { id: 'pos_q_028', priority: 173, pattern: /despesa.*offline|despesa.*pendente.*sync|despesa.*sincronizacao/, answer: 'Sem rede, a despesa pode ser guardada offline e enviada depois na sincronizacao.' },
  { id: 'pos_q_029', priority: 172, pattern: /toast.*despesa|mensagem.*despesa|feedback.*despesa/, answer: 'Fluxo de despesa possui toasts de sucesso, aviso (offline) e erro para orientar a operacao.' },
  { id: 'pos_q_030', priority: 171, pattern: /buscar.*despesa|pesquisa.*despesa|filtro.*texto.*despesa/, answer: 'A lista de despesas suporta pesquisa textual por nome e motivo.' },
  { id: 'pos_q_031', priority: 170, pattern: /filtro.*data inicial.*despesa|date from.*despesa|de.*ate.*despesas/, answer: 'Filtro de data inicial de despesas: reduz resultados para itens a partir da data informada.' },
  { id: 'pos_q_032', priority: 169, pattern: /filtro.*data final.*despesa|date to.*despesa/, answer: 'Filtro de data final de despesas: limita resultados ate a data informada.' },
  { id: 'pos_q_033', priority: 168, pattern: /cache.*despesas.*escopo|despesas.*empresa.*loja.*utilizador/, answer: 'Cache de despesas e segregado por empresa, loja e utilizador para evitar mistura de dados.' },
  { id: 'pos_q_034', priority: 167, pattern: /cache.*despesas.*datas|intervalo.*cache.*despesa/, answer: 'Existe chave de cache especifica para intervalo de datas das despesas.' },
  { id: 'pos_q_035', priority: 166, pattern: /pdf.*despesas.*sem dados|exportar.*despesas.*vazio/, answer: 'Exportacao PDF de despesas pode falhar/avisar quando nao existem dados no recorte atual.' },
  { id: 'pos_q_036', priority: 165, pattern: /preview.*pdf.*despesas|previsualizacao.*despesas/, answer: 'Preview de PDF de despesas abre em modal dedicado antes do download final.' },
  { id: 'pos_q_037', priority: 164, pattern: /download.*pdf.*despesas|baixar.*pdf.*despesas/, answer: 'Download do PDF exige URL de preview valida; sem isso, o POS alerta indisponibilidade.' },
  { id: 'pos_q_038', priority: 163, pattern: /fechar.*modal.*despesa|limpar.*filtros.*despesa/, answer: 'Ao fechar modal de despesas, o POS pode restaurar filtros/periodo padrao da operacao.' },
  { id: 'pos_q_039', priority: 162, pattern: /erro.*carregar.*despesa|falhou.*listar.*despesas|rede.*despesas/, answer: 'Falha de carga de despesas: valide conectividade e tente recarregar o modal.' },
  { id: 'pos_q_040', priority: 161, pattern: /duplicad.*despesa|deduplica.*despesa|despesa repetida/, answer: 'A lista aplica deduplicacao por UUID da despesa para evitar duplicidade visual.' },
  { id: 'pos_q_041', priority: 160, pattern: /existe.*venda ativa|venda iniciada|ha venda em curso/, answer: 'Venda ativa: o POS mantem estado da venda iniciada no escopo atual (empresa/loja).' },
  { id: 'pos_q_042', priority: 159, pattern: /numero.*venda ativa|identificador.*venda|id.*venda ativa/, answer: 'Identificador da venda ativa: consulte cabecalho/estado da venda iniciada no POS.' },
  { id: 'pos_q_043', priority: 158, pattern: /status.*venda ativa|estado.*venda iniciada/, answer: 'Status da venda ativa deve estar elegivel (ex.: iniciada) para operacoes de conclusao.' },
  { id: 'pos_q_044', priority: 157, pattern: /escopo.*venda.*sessao|empresa.*diverge.*venda|loja.*diverge.*venda/, answer: 'Escopo da venda e validado contra sessao. Divergencia de empresa/loja bloqueia finalizacao.' },
  { id: 'pos_q_045', priority: 156, pattern: /tipo.*documento.*diverge|mismatch.*documento|documento.*venda ativa/, answer: 'Se tipo de documento divergir da venda ativa, o POS pede reabertura/sincronizacao antes de finalizar.' },
  { id: 'pos_q_046', priority: 155, pattern: /recuperar.*venda ativa|venda anterior.*recuperada|recover.*sale/, answer: 'O POS tenta recuperar automaticamente venda ativa anterior quando o escopo e consistente.' },
  { id: 'pos_q_047', priority: 154, pattern: /resincronizar.*venda|sincronizar.*venda.*backend|sync.*active sale/, answer: 'A venda ativa pode ser re-sincronizada com backend para corrigir inconsistencias antes do fecho.' },
  { id: 'pos_q_048', priority: 153, pattern: /pode.*finalizar.*venda|bloqueio.*finalizacao|finalizar agora/, answer: 'Finalizacao exige: sessao valida, caixa aberta, escopo consistente, documento valido, itens e pagamento corretos.' },
  { id: 'pos_q_049', priority: 152, pattern: /pode.*cancelar.*venda|cancelar.*venda iniciada|cancelamento.*venda ativa/, answer: 'Cancelamento da venda iniciada depende do estado elegivel no backend para o escopo atual.' },
  { id: 'pos_q_050', priority: 151, pattern: /colocar.*em espera|venda.*em espera|hold.*sale/, answer: 'Colocar venda em espera e permitido apenas quando o backend confirma elegibilidade da venda ativa.' },
];

function escapeRegexText(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

const POS_CATEGORY_LABELS = {
  caixa: 'Caixa e fecho diario',
  despesas: 'Despesas POS',
  venda_ativa: 'Venda ativa',
  documento_licenca: 'Documento e licenciamento',
  carrinho_preco_stock: 'Carrinho, preco e stock',
  pagamento_finalizacao: 'Pagamento e finalizacao',
  cliente_agt: 'Cliente e AGT',
  transferencias: 'Transferencias entre lojas',
  offline_sync: 'Offline e sincronizacao',
  sessao_permissoes_ui: 'Sessao, permissoes e interface',
};

const POS_CATEGORY_NEXT_ACTION = {
  caixa: 'Valide abertura/fecho no bloco de caixa antes de continuar a operacao.',
  despesas: 'Abra o modal de despesas, aplique filtros e confirme o resumo do periodo.',
  venda_ativa: 'Confirme estado e escopo da venda ativa antes de finalizar/cancelar.',
  documento_licenca: 'Confira tipo de documento, serie e restricoes da licenca/plano.',
  carrinho_preco_stock: 'Revise item, quantidade, preco e stock antes de gravar a alteracao.',
  pagamento_finalizacao: 'Verifique metodo, valores recebidos e regras finais antes de concluir.',
  cliente_agt: 'Valide cliente selecionado e estado AGT antes de emitir documento fiscal.',
  transferencias: 'Revise quantidade, fator e estado do pedido entre origem e destino.',
  offline_sync: 'Confirme fila pendente e status de sincronizacao quando houver rede.',
  sessao_permissoes_ui: 'Verifique sessao ativa e permissoes para a acao desejada.',
};

function classifyIntentPromptStyle(text) {
  const normalized = normalizeLabelKey(text);
  if (!normalized) return 'operacional';

  if (/erro|falha|bloqueio|trav|nao funciona|inconsisten|causa|motivo|por que|porque|debug|diagnostico/.test(normalized)) {
    return 'diagnostico';
  }

  if (/como|passo|onde|abrir|fechar|configurar|fazer|executar|iniciar|selecionar/.test(normalized)) {
    return 'operacional';
  }

  return 'analitico';
}

function buildScopedIntentAnswer(intent, options = {}) {
  const style = String(options?.style || 'operacional').trim();
  const matchedInversion = Boolean(options?.matchedInversion);
  const id = String(intent?.id || 'intent_unknown').trim();
  const moduleName = String(intent?.moduleName || 'Modulo').trim();
  const question = String(intent?.question || '').trim();
  const inversion = String(intent?.inversion || '').trim();
  const source = String(intent?.source || 'fonte nao informada').trim();
  const nextAction = String(intent?.nextAction || 'Valide os dados no escopo atual antes de continuar.').trim();
  const promptMode = matchedInversion ? `inversao: "${inversion}"` : `pergunta: "${question}"`;

  if (style === 'diagnostico') {
    return `Entendi o cenário. No módulo ${moduleName}, valide primeiro ${promptMode} na fonte "${source}". Em seguida, execute esta ação: ${nextAction}`;
  }

  if (style === 'analitico') {
    return `Pelo contexto do módulo ${moduleName}, a referência principal para ${promptMode} é "${source}". Recomendação direta: ${nextAction}`;
  }

  return `Para ${promptMode} no módulo ${moduleName}, use "${source}" como referência. Próximo passo: ${nextAction}`;
}

function buildPosCatalogIntentAnswer(intent, options = {}) {
  const style = String(options?.style || 'operacional').trim();
  const matchedInversion = Boolean(options?.matchedInversion);
  const id = String(intent?.id || 'pos_q_unknown').trim();
  const question = String(intent?.question || '').trim();
  const inversion = String(intent?.inversion || '').trim();
  const source = String(intent?.source || 'fonte nao informada').trim();
  const categoryKey = String(intent?.category || 'sessao_permissoes_ui').trim();
  const categoryLabel = POS_CATEGORY_LABELS?.[categoryKey] || 'Operacao POS';
  const nextAction = POS_CATEGORY_NEXT_ACTION?.[categoryKey] || 'Valide os dados no escopo atual do POS antes de seguir.';
  const promptMode = matchedInversion ? `inversao: "${inversion}"` : `pergunta: "${question}"`;

  if (style === 'diagnostico') {
    return `Percebi. No POS (${categoryLabel}), confirme ${promptMode} usando "${source}" e verifique possíveis bloqueios de regra. Ação imediata: ${nextAction}`;
  }

  if (style === 'analitico') {
    return `No POS (${categoryLabel}), a referência principal para ${promptMode} é "${source}". Recomendação prática: ${nextAction}`;
  }

  return `No POS (${categoryLabel}), para ${promptMode}, siga "${source}". Próximo passo: ${nextAction}`;
}

function humanizeContextIntentAnswer(scope, intentId, answerText) {
  const cleanAnswer = String(answerText || '').trim();
  return cleanAnswer;
}

const POS_DYNAMIC_CATALOG_INTENT_RULES = (() => {
  const intents = Array.isArray(posIntentCatalog?.intents) ? posIntentCatalog.intents : [];
  if (!intents.length) return [];

  return intents
    .filter((intent) => String(intent?.scope || '').toLowerCase() === 'pos' && intent?.enabled !== false)
    .map((intent) => {
      const id = String(intent?.id || '').trim();
      const question = String(intent?.question || '').trim();
      const inversion = String(intent?.inversion || '').trim();
      const source = String(intent?.source || '').trim() || 'fonte nao informada';
      if (!id || !question || !inversion) return null;

      const qPattern = escapeRegexText(question).replace(/\s+/g, '\\s+');
      const invPattern = escapeRegexText(inversion).replace(/\s+/g, '\\s+');
      const priority = Math.max(20, Math.min(140, Number(intent?.priority || 0) - 60));

      return {
        id: `${id}_catalog`,
        priority,
        pattern: new RegExp(`(?:${qPattern}|${invPattern})`, 'i'),
        questionPattern: new RegExp(`^${qPattern}$`, 'i'),
        inversionPattern: new RegExp(`^${invPattern}$`, 'i'),
        resolveAnswer: (rawText) => {
          const style = classifyIntentPromptStyle(rawText);
          const textToCheck = String(rawText || '').trim();
          const matchedInversion = new RegExp(`^${invPattern}$`, 'i').test(textToCheck);
          return buildPosCatalogIntentAnswer({
            id,
            question,
            inversion,
            source,
            category: String(intent?.category || '').trim(),
          }, {
            style,
            matchedInversion,
          });
        },
      };
    })
    .filter(Boolean);
})();

const CLIENTS_TEMPLATE_INTENT_RULES = [
  {
    id: 'cli_create',
    priority: 100,
    pattern: /cadastrar|criar cliente/,
    question: 'Como cadastrar um cliente?',
    inversion: 'Nao consigo cadastrar cliente?',
    source: 'Cadastro de Clientes (/customer-registry)',
    nextAction: 'Abra o cadastro, preencha dados fiscais obrigatorios e grave no escopo correto da empresa/loja.',
    moduleName: 'Clientes',
  },
  {
    id: 'cli_validate_nif',
    priority: 95,
    pattern: /nif|agt|validar/,
    question: 'Como validar NIF na AGT?',
    inversion: 'NIF nao valida na AGT?',
    source: 'Validacao AGT do cliente (/customer-registry)',
    nextAction: 'Use a consulta AGT no cadastro do cliente, confirme status do contribuinte e so depois conclua o registo.',
    moduleName: 'Clientes',
  },
  {
    id: 'cli_statement_balance',
    priority: 90,
    pattern: /extrato|saldo|divida/,
    question: 'Como ver extrato e saldo do cliente?',
    inversion: 'Extrato/saldo do cliente nao bate?',
    source: 'Extrato de clientes (/customer-registry + extrato_clientes.php)',
    nextAction: 'Abra o cliente, confira lancamentos de debito/credito e valide o saldo em aberto no extrato.',
    moduleName: 'Clientes',
  },
].map((intent) => ({
  ...intent,
  resolveAnswer: (rawText) => {
    const style = classifyIntentPromptStyle(rawText);
    const text = String(rawText || '').trim();
    const matchedInversion = /nao|falha|erro|invalido|diverge|pendente/i.test(text);
    return buildScopedIntentAnswer(intent, { style, matchedInversion });
  },
}));

const SUPPLIERS_TEMPLATE_INTENT_RULES = [
  {
    id: 'sup_create',
    priority: 100,
    pattern: /cadastrar|criar fornecedor/,
    question: 'Como cadastrar um fornecedor?',
    inversion: 'Nao consigo cadastrar fornecedor?',
    source: 'Gestao de Fornecedores (/vendor-management)',
    nextAction: 'Abra fornecedores, preencha dados fiscais/comerciais e confirme o escopo antes de salvar.',
    moduleName: 'Fornecedores',
  },
  {
    id: 'sup_category',
    priority: 95,
    pattern: /categoria/,
    question: 'Como configurar categoria de fornecedor?',
    inversion: 'Categoria de fornecedor nao aplica?',
    source: 'Categorias de fornecedor (/vendor-management)',
    nextAction: 'Ajuste a categoria no cadastro do fornecedor e valide impacto em filtros e relatorios.',
    moduleName: 'Fornecedores',
  },
  {
    id: 'sup_contract',
    priority: 90,
    pattern: /contrato/,
    question: 'Como registrar contrato de fornecedor?',
    inversion: 'Contrato de fornecedor com inconsistencia?',
    source: 'Contratos em Gestao de Fornecedores',
    nextAction: 'Registe vigencia, valores e condicoes; depois confirme se o fornecedor ficou vinculado ao contrato correto.',
    moduleName: 'Fornecedores',
  },
].map((intent) => ({
  ...intent,
  resolveAnswer: (rawText) => {
    const style = classifyIntentPromptStyle(rawText);
    const text = String(rawText || '').trim();
    const matchedInversion = /nao|falha|erro|invalido|diverge|pendente/i.test(text);
    return buildScopedIntentAnswer(intent, { style, matchedInversion });
  },
}));

const CONTEXT_INTENT_RULES = {
  auth: [
    { id: 'auth_login_flow', priority: 100, pattern: /entrar|login|aceder|acesso/, answer: 'Para entrar no Dissa Ax: aceda à página de login, informe utilizador e palavra-passe, e confirme. Se falhar, valide primeiro a ligação e as credenciais.' },
    { id: 'auth_password_recovery', priority: 90, pattern: /senha|palavra passe|password|recuperar/, answer: 'Para recuperar acesso: use a opção de recuperação na tela de login ou solicite reset ao administrador. Depois, altere a palavra-passe no perfil.' },
  ],
  organization: [
    { id: 'org_subscription_plan', priority: 100, pattern: /plano|assinatura|licenca/, answer: 'Para assinatura: aceda ao módulo de assinaturas, valide o plano ativo, limites de lojas/dispositivos e período de validade antes de confirmar alterações.' },
    { id: 'org_limits_control', priority: 90, pattern: /limite|loja|dispositivo/, answer: 'Para limites: abra assinaturas da empresa e ajuste número de lojas/dispositivos. Em plano trial, os limites permanecem restritos conforme a regra ativa.' },
  ],
  dashboard: [
    { id: 'dash_kpi_reading', priority: 100, pattern: /kpi|indicador|interpretar|analise/, answer: 'Para interpretar o dashboard: compare Vendas, Receita do Mês e Ticket Médio; depois valide Stock Crítico e Dívidas para identificar risco operacional imediato.' },
    { id: 'dash_trend_compare', priority: 90, pattern: /queda|crescimento|tendencia/, answer: 'Para tendência: compare o período atual com o anterior no mesmo painel e confirme se a variação acompanha transações e ticket médio.' },
  ],
  transacoes: [
    { id: 'trx_proforma_issue', priority: 210, pattern: /proforma|pro forma|\bpp\b/, answer: PROFORMA_DETERMINISTIC_ANSWER },
    { id: 'trx_retificacao', priority: 100, pattern: /retificar|retificacao/, answer: 'Para retificar transação: abra Transações, selecione o documento, use a ação de retificação e confirme os campos fiscais antes de emitir.' },
    { id: 'trx_cancelamento', priority: 95, pattern: /cancelar/, answer: 'Para cancelamento: em Transações, selecione o documento, execute cancelar com motivo e confirme o estado final e sincronização fiscal.' },
    { id: 'trx_second_copy', priority: 90, pattern: /segunda via|2 via|reimprimir/, answer: 'Para segunda via: em Transações, abra o documento e use a opção de reimpressão/segunda via no formato necessário.' },
  ],
  produtos: [
    { id: 'prd_create', priority: 100, pattern: /criar|cadastrar|novo produto/, answer: 'Para cadastrar produto: vá ao Catálogo, clique em novo produto, preencha código/nome/unidade/imposto e grave no escopo correto da loja.' },
    { id: 'prd_min_stock', priority: 95, pattern: /stock minimo|estoque minimo/, answer: 'Para stock mínimo: abra o produto no Catálogo e ajuste o limite mínimo para ativar alertas de reposição.' },
    { id: 'prd_transfer', priority: 90, pattern: /transferencia|transferir/, answer: 'Para transferência: use o fluxo de movimentos de estoque, selecione loja origem/destino e confirme quantidades antes de concluir.' },
    { id: 'prd_conversion_factor', priority: 85, pattern: /fator conversao|conversao/, answer: 'Para fator de conversão: no catálogo, configure quantidade por unidade para cada produto e valide impacto no stock.' },
  ],
  clientes: [
    ...CLIENTS_TEMPLATE_INTENT_RULES,
  ],
  fornecedores: [
    ...SUPPLIERS_TEMPLATE_INTENT_RULES,
  ],
  pos: [
    { id: 'pos_proforma_issue', priority: 220, pattern: /proforma|pro forma|\bpp\b/, answer: PROFORMA_DETERMINISTIC_ANSWER },
    ...POS_PRIORITY_INTENT_RULES,
    ...POS_DYNAMIC_CATALOG_INTENT_RULES,
    { id: 'pos_new_sale', priority: 100, pattern: /iniciar venda|nova venda/, answer: 'No POS: selecione tipo de documento, adicione itens ao carrinho, escolha método de pagamento e finalize a venda.' },
    { id: 'pos_payment_method', priority: 95, pattern: /pagamento|metodo/, answer: 'No POS: em pagamento, escolha Numerário/TPA/Transferência conforme a operação e confirme o valor total pago.' },
    { id: 'pos_close_shift', priority: 90, pattern: /fechar caixa|relatorio diario/, answer: 'Para fecho de caixa: abra Relatório Diário, informe numerário físico e conclua o fecho para bloquear novas vendas do período já fechado.' },
    { id: 'pos_expense', priority: 85, pattern: /despesa/, answer: 'Para despesas POS: registre despesas apenas com relatório diário aberto, no escopo da loja e utilizador atual.' },
  ],
  cobrancas: [
    { id: 'col_register', priority: 100, pattern: /registrar cobranca|lancar cobranca/, answer: 'Para cobrança: aceda ao módulo de Cobranças, selecione cliente/documento pendente, informe o pagamento e confirme.' },
    { id: 'col_receipt', priority: 95, pattern: /recibo|emitir recibo/, answer: 'Para recibo: após registrar cobrança, use a opção de emissão para gerar o documento com os dados fiscais corretos.' },
    { id: 'col_reconciliation', priority: 90, pattern: /conciliar|reconciliar|tpa/, answer: 'Para conciliação: compare recebimentos por método (Numerário/TPA/Transferência) e ajuste divergências antes do fecho.' },
  ],
  configuracoes: [
    { id: 'cfg_permissions', priority: 100, pattern: /permissao|acesso|papel|perfil/, answer: 'Para permissões: no Controle de Acesso, ajuste permissões por papel/utilizador e valide o efeito com nova sessão.' },
    { id: 'cfg_company_data', priority: 95, pattern: /empresa|dados da empresa/, answer: 'Para dados da empresa: em Configurações, atualize identificação fiscal, contactos e parâmetros de documento.' },
    { id: 'cfg_profile', priority: 90, pattern: /perfil|preferencia/, answer: 'Para perfil: em Configurações de Perfil, atualize dados pessoais, palavra-passe e preferências operacionais.' },
  ],
  fiscal: [
    { id: 'fiscal_agt_setup', priority: 100, pattern: /configurar agt|integracao agt/, answer: 'Para AGT: configure credenciais/chaves, valide assinatura e teste consulta antes de emitir documentos em produção.' },
    { id: 'fiscal_invoice_status', priority: 95, pattern: /estado fatura|consultar fatura/, answer: 'Para consultar estado fiscal: use AGT Management, informe documento e execute consulta para retorno oficial do estado.' },
    { id: 'fiscal_document_validation', priority: 90, pattern: /validar documento|assinatura/, answer: 'Para validação: confirme série, assinatura e payload fiscal; só finalize quando o retorno da AGT indicar sucesso.' },
  ],
  admin: [
    { id: 'admin_observability', priority: 100, pattern: /observabilidade|evento|erro/, answer: 'Para observabilidade: use o painel de eventos para filtrar módulo/período, identificar falhas e exportar evidências.' },
    { id: 'admin_assistant_telemetry', priority: 95, pattern: /telemetria|assistente/, answer: 'Para telemetria do assistente: consulte Assistant Telemetry para volume, falhas e status de sincronização.' },
    { id: 'admin_export_report', priority: 90, pattern: /exportar|relatorio/, answer: 'Para exportação: aplique filtros no painel administrativo e exporte JSON/CSV conforme a necessidade de auditoria.' },
  ],
  global: [
    { id: 'global_proforma_issue', priority: 205, pattern: /como emitir.*proforma|emitir.*proforma|proforma|pro forma|\bpp\b/, answer: PROFORMA_DETERMINISTIC_ANSWER },
  ],
};

const PAGE_CONTEXT_RULES = [
  {
    test: (path) => path === '/landing' || path === '/splash' || path === '/login' || path === '/register',
    pageName: 'Acesso e Entrada',
    context: 'O utilizador esta na area de acesso. Priorize orientacoes sobre login, recuperacao de acesso e primeiros passos no sistema.',
    suggestions: [
      'Como entrar no sistema?',
      'Como resolver erro de autenticação?',
      'Como criar conta de utilizador?',
      'Como trocar palavra-passe?',
    ],
    scope: 'auth',
  },
  {
    test: (path) => path === '/company-setup' || path === '/subscription-plans' || path === '/admin-licencas-assinatura',
    pageName: 'Configuração da Empresa e Assinatura',
    context: 'O utilizador esta na area de configuracao empresarial. Priorize orientacoes sobre onboarding, assinatura e limites de uso.',
    suggestions: [
      'Como concluir a configuração da empresa?',
      'Como escolher o plano de assinatura?',
      'Como validar limites de lojas e dispositivos?',
      'Como atualizar licenças?',
    ],
    scope: 'organization',
  },
  {
    test: (path) => path === '/manager-overview' || path === '/ceo-dashboard' || path === '/store-manager-dashboard' || path === '/executive-kpi-dashboard' || path === '/sales-analytics-dashboard',
    pageName: 'Dashboards e KPI',
    context: 'O utilizador esta em dashboards de gestao. Priorize leitura de indicadores, comparativos e acoes praticas com base nos dados exibidos.',
    suggestions: [
      'Como interpretar os KPIs principais?',
      'Como comparar desempenho por período?',
      'Como identificar quedas de vendas?',
      'Como exportar análise para relatório?',
    ],
    scope: 'dashboard',
  },
  {
    test: (path) => path === '/transacoes' || path.startsWith('/transacoes/'),
    pageName: 'Transações',
    context: 'O utilizador esta na area de Transacoes. Priorize ajuda para consultar documentos, ajustes (retificar/cancelar), validacao AGT e segunda via.',
    suggestions: [
      'Como retificar uma transação?',
      'Como cancelar uma venda de forma correta?',
      'Como emitir segunda via do documento?',
      'Como validar documento na AGT?',
    ],
    scope: 'transacoes',
  },
  {
    test: (path) => path === '/product-catalog' || path.startsWith('/product-catalog/'),
    pageName: 'Catálogo de Produtos',
    context: 'O utilizador esta na area de Produtos. Priorize ajuda sobre cadastro, stock minimo, fatores de conversao e movimentacoes.',
    suggestions: [
      'Como criar um produto novo?',
      'Como ajustar stock minimo?',
      'Como configurar fator de conversao?',
      'Como fazer transferencia de estoque?',
    ],
    scope: 'produtos',
  },
  {
    test: (path) => path === '/customer-registry' || path.startsWith('/customer-registry/'),
    pageName: 'Cadastro de Clientes',
    context: 'O utilizador esta na area de clientes. Priorize orientacoes sobre cadastro, validacao fiscal AGT e gestão de crédito.',
    suggestions: [
      'Como cadastrar um cliente?',
      'Como validar NIF na AGT?',
      'Como ajustar limite de crédito?',
      'Como consultar extrato do cliente?',
    ],
    scope: 'clientes',
  },
  {
    test: (path) => path === '/vendor-management' || path.startsWith('/vendor-management/'),
    pageName: 'Gestão de Fornecedores',
    context: 'O utilizador esta na area de fornecedores. Priorize orientacoes sobre cadastro, categorias, contratos e conformidade de dados.',
    suggestions: [
      'Como cadastrar um fornecedor?',
      'Como criar categoria de fornecedor?',
      'Como registrar um contrato?',
      'Como atualizar regime tributário?',
    ],
    scope: 'fornecedores',
  },
  {
    test: (path) => path === '/pos-terminal' || path.startsWith('/pos-terminal/') || path === '/pos-relatorio-diario',
    pageName: 'POS e Relatório Diário',
    context: 'O utilizador esta no POS. Priorize respostas curtas e operacionais para abertura de caixa, venda, pagamento e fechamento diario.',
    suggestions: [
      'Como iniciar uma venda no POS?',
      'Como trocar método de pagamento?',
      'Como aplicar desconto por item?',
      'Como fechar caixa no relatório diário?',
    ],
    scope: 'pos',
  },
  {
    test: (path) => path === '/cobrancas' || path.startsWith('/cobrancas/'),
    pageName: 'Cobranças',
    context: 'O utilizador esta na area de cobrancas. Priorize ajuda sobre recebimentos, conciliacao de pagamentos e emissao de recibos.',
    suggestions: [
      'Como registrar uma cobrança?',
      'Como emitir recibo de pagamento?',
      'Como conferir pagamentos pendentes?',
      'Como reconciliar TPA e numerário?',
    ],
    scope: 'cobrancas',
  },
  {
    test: (path) => path === '/access-control' || path === '/settings' || path === '/profile-settings',
    pageName: 'Configurações e Acessos',
    context: 'O utilizador esta na area de configuracoes. Priorize orientacoes sobre permissões, perfis, preferências e segurança de conta.',
    suggestions: [
      'Como ajustar permissões de utilizadores?',
      'Como configurar dados da empresa?',
      'Como alterar preferências do perfil?',
      'Como reforçar segurança de acesso?',
    ],
    scope: 'configuracoes',
  },
  {
    test: (path) => path === '/agt-management' || path === '/financial-compliance-hub',
    pageName: 'AGT e Compliance Fiscal',
    context: 'O utilizador esta em modulo fiscal/AGT. Priorize orientacoes sobre assinatura, consulta fiscal e conformidade dos documentos.',
    suggestions: [
      'Como configurar integração AGT?',
      'Como consultar estado de fatura?',
      'Como validar documento na AGT?',
      'Como resolver falhas de assinatura?',
    ],
    scope: 'fiscal',
  },
  {
    test: (path) => path === '/admin-painel' || path === '/observability-events' || path === '/assistant-telemetry',
    pageName: 'Administração e Observabilidade',
    context: 'O utilizador esta em area administrativa. Priorize orientacoes sobre monitorizacao, auditoria e gestão operacional do sistema.',
    suggestions: [
      'Como monitorar eventos do sistema?',
      'Como analisar erros recentes?',
      'Como acompanhar adoção do assistente?',
      'Como exportar telemetria?',
    ],
    scope: 'admin',
  },
];

function titleFromPath(pathname) {
  const normalizedPath = String(pathname || '').replace(/\/+$/, '') || '/';
  const segments = normalizedPath.split('/').filter(Boolean);
  if (!segments.length) return 'Início';

  const label = segments[0]
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());

  return label || 'Módulo';
}

function readAuthSessionSnapshot() {
  if (typeof window === 'undefined') {
    return {
      userKey: 'anonymous',
      userName: 'Utilizador',
      token: '',
      companyUuid: '',
      storeUuid: '',
    };
  }

  try {
    const raw = window.sessionStorage.getItem('angolabi.erp.authSession') || 'null';
    const parsed = JSON.parse(raw);
    const user = parsed?.currentUser || {};
    const userId = String(user?.id || user?.uuid || user?.uid || '').trim();

    return {
      userKey: userId || 'anonymous',
      userName: String(user?.name || user?.nome || 'Utilizador').trim() || 'Utilizador',
      token: String(parsed?.token || '').trim(),
      roleValue: String(user?.roleValue || user?.role || '').trim().toLowerCase(),
      companyUuid: String(user?.companyUuid || '').trim(),
      storeUuid: String(user?.storeUuid || '').trim(),
    };
  } catch {
    return {
      userKey: 'anonymous',
      userName: 'Utilizador',
      token: '',
      roleValue: '',
      companyUuid: '',
      storeUuid: '',
    };
  }
}

function isBusinessDataQuestion(text) {
  const normalized = String(text || '').toLowerCase();
  if (!normalized) return false;

  return /venda|vendas|fatura[cç][aã]o|faturacao|receita|desempenho|ticket|m[eê]s|meses|seis meses|6 meses|kpi|meta|crescimento|comparar|tend[eê]ncia|relat[oó]rio|produto|produtos|cat[aá]logo|catalogo|estoque|stock|sku|fornecedor|fornecedores|cliente|clientes|cobranca|cobrancas|pagamento|pagamentos|divida|dividas|contrato|contratos|transac[aã]o|transacoes|extrato|movimento|nif|agt|pendencia fiscal|credito exposto|offline|sincroniza|endpoint|api|toast|mensagem de erro|mysql|certifica[cç][aã]o|certificado|valida[cç][aã]o.*agt|n[uú]mero.*certifica[cç][aã]o|ano.*certifica[cç][aã]o/i.test(normalized);
}

function asksProductCount(text) {
  const normalized = String(text || '').toLowerCase();
  if (!normalized) return false;

  return /quant[oa]s?.*(produtos|itens)|total.*produtos|n[uú]mero.*produtos|quantidade.*produtos|produtos.*cadastrad/i.test(normalized);
}

function normalizeLabelKey(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function findKpiByLabel(snapshot, matcher) {
  const list = Array.isArray(snapshot?.kpis) ? snapshot.kpis : [];
  return list.find((kpi) => matcher(normalizeLabelKey(kpi?.label)));
}

function formatTrendPoint(point) {
  if (!point) return '';
  const label = String(point?.label || 'período').trim();
  const revenue = Number(point?.revenue || 0);
  const transactions = Number(point?.transactions || 0);
  return `${label}: receita ${revenue.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA e ${transactions} transações`;
}

function isLikelyDailyLabel(label) {
  const normalized = normalizeLabelKey(label);
  if (!normalized) return false;

  return /\bseg\b|\bter\b|\bqua\b|\bqui\b|\bsex\b|\bsab\b|\bdom\b|segunda|terca|quarta|quinta|sexta|sabado|domingo|^\d{1,2}$|^\d{4}\s\d{2}\s\d{2}$/.test(normalized);
}

const STRICT_DETERMINISTIC_ANSWER_TYPES = new Set([
  'best_sales_day_current_month',
  'best_sales_day_unavailable',
  'software_certification_year',
  'software_certification_year_unavailable',
  'software_certification_number',
  'software_certification_number_unavailable',
]);

function getTrendStats(snapshot) {
  const trend = Array.isArray(snapshot?.trend) ? snapshot.trend : [];
  if (!trend.length) {
    return null;
  }

  let best = trend[0];
  let worst = trend[0];
  for (const row of trend) {
    if (Number(row?.revenue || 0) > Number(best?.revenue || 0)) best = row;
    if (Number(row?.revenue || 0) < Number(worst?.revenue || 0)) worst = row;
  }

  const first = trend[0];
  const last = trend[trend.length - 1];
  const firstRevenue = Number(first?.revenue || 0);
  const lastRevenue = Number(last?.revenue || 0);
  const delta = lastRevenue - firstRevenue;
  const pct = firstRevenue > 0 ? (delta / firstRevenue) * 100 : 0;

  return {
    best,
    worst,
    first,
    last,
    delta,
    pct,
    points: trend.length,
  };
}

function buildDeterministicBusinessAnswer(text, businessSummary) {
  const normalized = normalizeLabelKey(text);
  const totalProducts = Number(businessSummary?.catalogMetrics?.totalProducts || 0);
  const goodsProducts = Number(businessSummary?.catalogMetrics?.goodsProducts || 0);
  const serviceProducts = Number(businessSummary?.catalogMetrics?.serviceProducts || 0);
  const customersMetrics = businessSummary?.moduleMetrics?.customers || {};
  const suppliersMetrics = businessSummary?.moduleMetrics?.suppliers || {};
  const salesMetrics = businessSummary?.moduleMetrics?.sales || {};
  const complianceMetrics = businessSummary?.moduleMetrics?.compliance || {};
  const totalCustomers = Number(customersMetrics?.totalCustomers || 0);
  const activeCustomers = Number(customersMetrics?.activeCustomers || 0);
  const inactiveCustomers = Number(customersMetrics?.inactiveCustomers || 0);
  const agtValidatedCustomers = Number(customersMetrics?.agtValidatedCustomers || 0);
  const agtPendingCustomers = Number(customersMetrics?.agtPendingCustomers || 0);
  const debtorCustomersCount = Number(customersMetrics?.debtorCustomersCount || 0);
  const totalOpenBalance = Number(customersMetrics?.totalOpenBalance || 0);
  const totalCredit = Number(customersMetrics?.totalCredit || 0);
  const topDebtorName = String(customersMetrics?.topDebtorName || '').trim();
  const topDebtorOpenBalance = Number(customersMetrics?.topDebtorOpenBalance || 0);
  const topDebtors = Array.isArray(customersMetrics?.topDebtors)
    ? customersMetrics.topDebtors
      .map((row) => ({
        name: String(row?.name || '').trim(),
        openBalance: Number(row?.openBalance || 0),
      }))
      .filter((row) => row.name && row.openBalance > 0)
      .slice(0, 3)
    : [];
  const totalSuppliers = Number(suppliersMetrics?.totalSuppliers || 0);
  const activeSuppliers = Number(suppliersMetrics?.activeSuppliers || 0);
  const averageSupplierRating = Number(suppliersMetrics?.averageRating || 0);
  const totalContractsValue = Number(suppliersMetrics?.totalContractsValue || 0);
  const totalDocuments = Number(salesMetrics?.totalDocuments || 0);
  const totalRevenue = Number(salesMetrics?.totalRevenue || 0);
  const totalPaid = Number(salesMetrics?.totalPaid || 0);
  const totalPending = Number(salesMetrics?.totalPending || 0);
  const averageTicketMetric = Number(salesMetrics?.averageTicket || 0);
  const bestSalesDayCurrentMonth = salesMetrics?.bestSalesDayCurrentMonth || null;
  const worstSalesDayCurrentMonth = salesMetrics?.worstSalesDayCurrentMonth || null;
  const peakSalesHourCurrentMonth = salesMetrics?.peakSalesHourCurrentMonth || null;
  const topProductCurrentMonth = salesMetrics?.topProductCurrentMonth || null;
  const topProductsCurrentMonth = Array.isArray(salesMetrics?.topProductsCurrentMonth)
    ? salesMetrics.topProductsCurrentMonth
    : [];
  const topCustomerCurrentMonth = salesMetrics?.topCustomerCurrentMonth || null;
  const topPaymentMethodCurrentMonth = salesMetrics?.topPaymentMethodCurrentMonth || null;
  const certificationYear = String(complianceMetrics?.certificationYear || '').trim();
  const certificationNumber = String(complianceMetrics?.softwareValidationNumber || '').trim();
  const trendStats = getTrendStats(businessSummary);

  if (asksProductCount(text)) {
    return {
      type: 'products_count',
      text: Number.isFinite(totalProducts) && totalProducts >= 0
        ? `No seu escopo atual, você tem ${totalProducts} produtos cadastrados (${goodsProducts} bens e ${serviceProducts} serviços).`
        : 'Nao consegui confirmar o total de produtos no seu escopo agora.',
    };
  }

  if (/quantos.*bens|total.*bens|quantos.*servicos|total.*servicos|mix.*produtos|distribuicao.*produtos/.test(normalized)) {
    return {
      type: 'products_mix',
      text: `No seu escopo atual, o catálogo está dividido em ${goodsProducts} bens e ${serviceProducts} serviços, totalizando ${totalProducts} produtos.`,
    };
  }

  const asksMonthlyRevenue = /receita.*mes|faturacao.*mes|faturacao.*mensal|quanto.*faturei.*mes/.test(normalized);
  if (asksMonthlyRevenue) {
    const monthlyRevenueKpi = findKpiByLabel(businessSummary, (label) => label.includes('receita do mes'));
    if (monthlyRevenueKpi?.value) {
      return {
        type: 'monthly_revenue',
        text: `No seu escopo atual, a receita do mês é ${monthlyRevenueKpi.value}.`,
      };
    }
  }

  const asksSales = /quantas?.*vendas|numero.*vendas|total.*vendas|vendas.*hoje|vendas.*periodo/.test(normalized);
  if (asksSales) {
    const salesKpi = findKpiByLabel(businessSummary, (label) => label.includes('vendas'));
    if (salesKpi?.value) {
      return {
        type: 'sales_indicator',
        text: `No seu escopo atual, o indicador de vendas está em ${salesKpi.value}.`,
      };
    }
  }

  const asksTicket = /ticket medio|ticket|valor medio.*venda|media.*venda/.test(normalized);
  if (asksTicket) {
    const ticketKpi = findKpiByLabel(businessSummary, (label) => label.includes('ticket medio'));
    if (ticketKpi?.value) {
      return {
        type: 'ticket_average',
        text: `No seu escopo atual, o ticket médio é ${ticketKpi.value}.`,
      };
    }
  }

  const asksStock = /stock critico|estoque critico|stock baixo|estoque baixo/.test(normalized);
  if (asksStock) {
    const stockKpi = findKpiByLabel(businessSummary, (label) => label.includes('stock critico'));
    if (stockKpi?.value) {
      return {
        type: 'stock_critical',
        text: `No seu escopo atual, o indicador de stock crítico é ${stockKpi.value}.`,
      };
    }
  }

  const asksClients = /quantos.*clientes|numero.*clientes|total.*clientes|clientes.*hoje|clientes.*periodo/.test(normalized);
  if (asksClients) {
    return {
      type: 'clients_indicator',
      text: `No seu escopo atual, há ${totalCustomers} clientes cadastrados, sendo ${activeCustomers} ativos.`,
    };
  }

  if (/nao.*tenho.*clientes|sem.*clientes|tenho.*algum.*cliente/.test(normalized)) {
    return {
      type: 'clients_exists_check',
      text: totalCustomers > 0
        ? `No seu escopo atual, existem ${totalCustomers} clientes cadastrados.`
        : 'No seu escopo atual, não existem clientes cadastrados.',
    };
  }

  if (/clientes.*ativos|ativos.*clientes|clientes.*inativos|inativos.*clientes/.test(normalized)) {
    return {
      type: 'clients_active_inactive',
      text: `No seu escopo atual, há ${activeCustomers} clientes ativos e ${inactiveCustomers} inativos.`,
    };
  }

  if (/nif.*validado|percentagem.*nif|taxa.*nif/.test(normalized)) {
    const rate = totalCustomers > 0 ? (agtValidatedCustomers / totalCustomers) * 100 : 0;
    return {
      type: 'clients_nif_rate',
      text: `No seu escopo atual, ${agtValidatedCustomers} de ${totalCustomers} clientes estão com NIF validado (${rate.toFixed(1)}%).`,
    };
  }

  if (/pendencia.*fiscal|pendencias.*fiscais|clientes.*pendentes.*agt/.test(normalized)) {
    return {
      type: 'clients_fiscal_pending',
      text: `No seu escopo atual, há ${agtPendingCustomers} clientes com pendência fiscal (AGT).`,
    };
  }

  if (/credito exposto|exposicao de credito|risco de credito/.test(normalized)) {
    return {
      type: 'clients_credit_exposure',
      text: `No seu escopo atual, o crédito exposto é ${totalOpenBalance.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA, com crédito concedido total de ${totalCredit.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA.`,
    };
  }

  if (/quantos.*clientes.*divida|clientes.*com.*divida|devedores.*quantos/.test(normalized)) {
    return {
      type: 'clients_debtors_count',
      text: `No seu escopo atual, há ${debtorCustomersCount} clientes com dívida em aberto.`,
    };
  }

  if (/qual.*cliente.*mais.*divida|cliente.*mais.*devedor|maior.*divida.*cliente|cliente.*maior.*divida|cliente.*mais.*devidas/.test(normalized)) {
    if (topDebtorName && topDebtorOpenBalance > 0) {
      return {
        type: 'top_debtor_customer',
        text: `No seu escopo atual, o cliente com maior dívida é ${topDebtorName}, com saldo em aberto de ${topDebtorOpenBalance.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA.`,
      };
    }

    return {
      type: 'top_debtor_customer_unavailable',
      text: 'No seu escopo atual, não há cliente com dívida em aberto identificável neste momento.',
    };
  }

  if (/top\s*3.*clientes.*divida|3.*clientes.*maior.*divida|clientes.*mais.*devedores|top.*clientes.*devedor/.test(normalized)) {
    if (topDebtors.length > 0) {
      const rankingText = topDebtors
        .map((item, index) => `${index + 1}) ${item.name}: ${item.openBalance.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA`)
        .join(' | ');

      return {
        type: 'top_debtor_customers',
        text: `No seu escopo atual, os clientes com maior dívida são: ${rankingText}.`,
      };
    }

    return {
      type: 'top_debtor_customers_unavailable',
      text: 'No seu escopo atual, não há clientes com dívida em aberto para formar um top 3.',
    };
  }

  if (/divida|dividas|em atraso|atraso.*clientes/.test(normalized)) {
    return {
      type: 'clients_debt',
      text: `No seu escopo atual, o saldo em aberto de clientes é ${totalOpenBalance.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA, com crédito total de ${totalCredit.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA.`,
    };
  }

  if (/endpoint.*clientes|api.*clientes|rota.*clientes/.test(normalized)) {
    return {
      type: 'technical_endpoint_customers',
      text: 'Determinístico técnico: o módulo de clientes usa GET /clientes.php para listagem e POST/PUT /clientes.php para gravação.',
    };
  }

  if (/endpoint.*extrato|api.*extrato|rota.*extrato/.test(normalized)) {
    return {
      type: 'technical_endpoint_statement',
      text: 'Determinístico técnico: o extrato usa GET /extrato_clientes.php para listagem e POST/PUT /extrato_clientes.php para lançamentos.',
    };
  }

  if (/endpoint.*agt|api.*agt|consulta.*agt.*nif/.test(normalized)) {
    return {
      type: 'technical_endpoint_agt',
      text: 'Determinístico técnico: a validação de contribuinte é feita por POST /agt_contribuinte.php com NIF normalizado.',
    };
  }

  if (/em que ano.*sistema.*certific|ano.*certifica[cç][aã]o|quando.*sistema.*certific/.test(normalized)) {
    if (certificationYear) {
      return {
        type: 'software_certification_year',
        text: `O sistema foi certificado em ${certificationYear}.`,
      };
    }

    return {
      type: 'software_certification_year_unavailable',
      text: 'Determinístico fiscal: não encontrei o ano de certificação no produtor de software ativo. Verifique o cadastro em AGT Management.',
    };
  }

  if (/numero.*certifica[cç][aã]o|n[uú]mero.*valida[cç][aã]o|software.*validado/.test(normalized)) {
    if (certificationNumber) {
      return {
        type: 'software_certification_number',
        text: `O numero da certificação é ${certificationNumber}.`,
      };
    }

    return {
      type: 'software_certification_number_unavailable',
      text: 'Determinístico fiscal: não encontrei o número de certificação no produtor de software ativo. Verifique o cadastro em AGT Management.',
    };
  }

  if (/validac|regras.*cliente|nif invalido|codigo duplicado|nif duplicado/.test(normalized)) {
    return {
      type: 'technical_validation_rules',
      text: 'Determinístico técnico: regras principais de gravação de cliente incluem empresa obrigatória, nome obrigatório, NIF obrigatório e válido, e unicidade de código e NIF no escopo.',
    };
  }

  if (/toast|mensagem.*erro|mensagem.*sucesso|feedback.*clientes/.test(normalized)) {
    return {
      type: 'technical_toasts_catalog',
      text: 'Determinístico técnico: a tela possui toasts de sucesso/erro/aviso para carga, validação AGT, gravação, exportação PDF/CSV e sincronização offline.',
    };
  }

  if (/offline|sincronizacao offline|fila de sincronizacao|mysql.*voltar/.test(normalized)) {
    return {
      type: 'technical_offline_sync',
      text: 'Determinístico técnico: no modo offline, clientes podem ser salvos localmente e entram em fila; ao voltar a conexão, a sincronização envia para o MySQL automaticamente.',
    };
  }

  if (/quantos.*fornecedores|numero.*fornecedores|total.*fornecedores/.test(normalized)) {
    return {
      type: 'suppliers_count',
      text: `No seu escopo atual, há ${totalSuppliers} fornecedores cadastrados, sendo ${activeSuppliers} ativos.`,
    };
  }

  if (/rating.*fornecedor|avaliacao.*fornecedor|desempenho.*fornecedor/.test(normalized)) {
    return {
      type: 'suppliers_rating',
      text: `No seu escopo atual, o rating médio de fornecedores é ${averageSupplierRating.toFixed(2)} e o valor total de contratos é ${totalContractsValue.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA.`,
    };
  }

  if (/quantas.*transacoes|numero.*transacoes|total.*documentos|documentos emitidos/.test(normalized)) {
    return {
      type: 'documents_count',
      text: `No seu escopo atual, foram registados ${totalDocuments} documentos no período analisado.`,
    };
  }

  if (/quanto.*recebido|total.*recebido|pagamentos recebidos|valor pago/.test(normalized)) {
    return {
      type: 'payments_received',
      text: `No seu escopo atual, o total recebido no período é ${totalPaid.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA.`,
    };
  }

  if (/pendente|por receber|em aberto.*pagamento|valor pendente|cobranca.*pendente/.test(normalized)) {
    return {
      type: 'payments_pending',
      text: `No seu escopo atual, o valor pendente de recebimento no período é ${totalPending.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA.`,
    };
  }

  if (/faturamento total|receita total|valor total vendido/.test(normalized)) {
    return {
      type: 'total_revenue',
      text: `No seu escopo atual, o valor total vendido no período é ${totalRevenue.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA.`,
    };
  }

  if (/equipa|equipe|presente|ausente|colaborador/.test(normalized)) {
    const teamKpi = findKpiByLabel(businessSummary, (label) => label.includes('equipa presente'));
    if (teamKpi?.value) {
      return {
        type: 'team_presence',
        text: `No seu escopo atual, a equipa presente está em ${teamKpi.value}.`,
      };
    }
  }

  if (/meta mensal|cumprimento.*meta|percentual.*meta|progresso.*meta/.test(normalized)) {
    const goalKpi = findKpiByLabel(businessSummary, (label) => label.includes('meta mensal'));
    if (goalKpi?.value) {
      return {
        type: 'monthly_goal',
        text: `No seu escopo atual, o cumprimento da meta mensal está em ${goalKpi.value}.`,
      };
    }
  }

  if (/kpi|indicadores|resumo.*negocio|resumo.*loja|resumo.*kpi/.test(normalized)) {
    const summaryList = (Array.isArray(businessSummary?.kpis) ? businessSummary.kpis : [])
      .slice(0, 5)
      .map((kpi) => `${kpi.label}: ${kpi.value}`)
      .filter(Boolean);

    if (summaryList.length) {
      return {
        type: 'kpi_summary',
        text: `Resumo atual do seu escopo: ${summaryList.join(' | ')}.`,
      };
    }
  }

  if (/tendencia|tendencia de vendas|evolucao|evolucao de vendas|crescimento|queda|subiu|desceu/.test(normalized) && trendStats) {
    const direction = trendStats.delta > 0 ? 'crescimento' : (trendStats.delta < 0 ? 'queda' : 'estabilidade');
    const pctText = Number.isFinite(trendStats.pct) ? `${trendStats.pct.toFixed(2)}%` : '0.00%';
    return {
      type: 'sales_trend',
      text: `No seu escopo atual, a tendência recente indica ${direction} de ${pctText} entre ${trendStats.first?.label || 'início'} e ${trendStats.last?.label || 'fim'}. Melhor período: ${formatTrendPoint(trendStats.best)}.`,
    };
  }

  if (/qual.*dia.*ma(is|s).*vend|dia.*ma(is|s).*venda|dia.*com.*maior.*venda|melhor.*dia.*venda|dia.*vendemos.*mes.*corrente|mes.*corrente.*dia.*venda/.test(normalized)) {
    const bestDayRevenue = Number(bestSalesDayCurrentMonth?.revenue || 0);
    const bestDayTransactions = Number(bestSalesDayCurrentMonth?.transactions || 0);
    const bestDayLabel = String(bestSalesDayCurrentMonth?.label || bestSalesDayCurrentMonth?.date || '').trim();

    if (bestDayLabel && (bestDayRevenue > 0 || bestDayTransactions > 0)) {
      return {
        type: 'best_sales_day_current_month',
        text: `No seu escopo atual, o dia com maior venda no mês corrente é ${bestDayLabel}, com receita de ${bestDayRevenue.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA e ${bestDayTransactions} transações.`,
      };
    }

    const trendRows = Array.isArray(businessSummary?.trend) ? businessSummary.trend : [];
    const hasDailyGranularity = trendRows.some((row) => isLikelyDailyLabel(row?.label));

    if (!hasDailyGranularity) {
      return {
        type: 'best_sales_day_unavailable',
        text: 'No seu escopo atual, o snapshot disponível está agregado por período (ex.: mês) e não por dia. Para apontar o dia com maior venda no mês corrente, preciso da série diária carregada no dashboard de vendas do período atual.',
      };
    }

    const bestRevenue = Number(trendStats?.best?.revenue || 0);
    const bestTransactions = Number(trendStats?.best?.transactions || 0);
    if (!trendStats?.best || (bestRevenue <= 0 && bestTransactions <= 0)) {
      return {
        type: 'best_sales_day_unavailable',
        text: 'No seu escopo atual, não há movimento diário suficiente no mês corrente para identificar um dia com maior venda.',
      };
    }

    if (trendStats?.best) {
      return {
        type: 'best_sales_day_current_month',
        text: `No seu escopo atual, o dia com maior venda no mês corrente é ${trendStats.best.label}, com receita de ${Number(trendStats.best.revenue || 0).toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA e ${Number(trendStats.best.transactions || 0)} transações.`,
      };
    }

    return {
      type: 'best_sales_day_unavailable',
      text: 'No seu escopo atual, não tenho série diária suficiente neste snapshot para apontar o melhor dia do mês corrente. Para confirmar, consulte o dashboard de vendas do período atual ou o relatório diário do POS.',
    };
  }

  if (/qual.*dia.*menor.*vend|dia.*menor.*venda|pior.*dia.*venda|dia.*mais.*fraco/.test(normalized)) {
    const worstDayRevenue = Number(worstSalesDayCurrentMonth?.revenue || 0);
    const worstDayTransactions = Number(worstSalesDayCurrentMonth?.transactions || 0);
    const worstDayLabel = String(worstSalesDayCurrentMonth?.label || worstSalesDayCurrentMonth?.date || '').trim();

    if (worstDayLabel && (worstDayRevenue > 0 || worstDayTransactions > 0)) {
      return {
        type: 'worst_sales_day_current_month',
        text: `No seu escopo atual, o dia com menor venda no mês corrente é ${worstDayLabel}, com receita de ${worstDayRevenue.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA e ${worstDayTransactions} transações.`,
      };
    }

    return {
      type: 'worst_sales_day_unavailable',
      text: 'No seu escopo atual, não há dados suficientes para identificar o dia com menor venda no mês corrente.',
    };
  }

  if (/hora.*pico|horario.*pico|que horas.*vendemos mais|hora.*maior.*venda|periodo.*maior.*movimento/.test(normalized)) {
    const peakLabel = String(peakSalesHourCurrentMonth?.label || '').trim();
    const peakRevenue = Number(peakSalesHourCurrentMonth?.revenue || 0);
    const peakTransactions = Number(peakSalesHourCurrentMonth?.transactions || 0);

    if (peakLabel && (peakRevenue > 0 || peakTransactions > 0)) {
      return {
        type: 'peak_sales_hour_current_month',
        text: `No seu escopo atual, o horário de pico no mês corrente é ${peakLabel}, com receita de ${peakRevenue.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA e ${peakTransactions} transações.`,
      };
    }

    return {
      type: 'peak_sales_hour_unavailable',
      text: 'No seu escopo atual, não há dados suficientes para identificar o horário de pico no mês corrente.',
    };
  }

  if (/produto.*mais.*vendido|item.*mais.*vendido|produto.*lider.*venda/.test(normalized)) {
    const topProductName = String(topProductCurrentMonth?.name || '').trim();
    const topProductQuantity = Number(topProductCurrentMonth?.quantity || 0);
    const topProductRevenue = Number(topProductCurrentMonth?.revenue || 0);

    if (topProductName && (topProductQuantity > 0 || topProductRevenue > 0)) {
      return {
        type: 'top_product_current_month',
        text: `No seu escopo atual, o produto mais vendido no mês corrente é ${topProductName}, com ${topProductQuantity.toLocaleString('pt-AO', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} unidades e ${topProductRevenue.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA de faturação.`,
      };
    }

    return {
      type: 'top_product_unavailable',
      text: 'No seu escopo atual, não há dados suficientes para identificar o produto mais vendido no mês corrente.',
    };
  }

  if (/top\s*3.*produt|3.*produt.*mais.*vend|top.*produt.*mes/.test(normalized)) {
    if (topProductsCurrentMonth.length > 0) {
      const ranking = topProductsCurrentMonth
        .slice(0, 3)
        .map((item, index) => `${index + 1}) ${String(item?.name || 'Produto')}: ${Number(item?.quantity || 0).toLocaleString('pt-AO', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} un. (${Number(item?.revenue || 0).toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA)`)
        .join(' | ');

      return {
        type: 'top_products_current_month',
        text: `No seu escopo atual, o top 3 de produtos no mês corrente é: ${ranking}.`,
      };
    }

    return {
      type: 'top_products_unavailable',
      text: 'No seu escopo atual, não há dados suficientes para listar o top 3 de produtos no mês corrente.',
    };
  }

  if (/cliente.*mais.*comprou|cliente.*maior.*compra|top.*cliente.*comprou|cliente.*lider.*faturacao/.test(normalized)) {
    const topCustomerName = String(topCustomerCurrentMonth?.name || '').trim();
    const topCustomerDocuments = Number(topCustomerCurrentMonth?.documents || 0);
    const topCustomerRevenue = Number(topCustomerCurrentMonth?.revenue || 0);

    if (topCustomerName && (topCustomerDocuments > 0 || topCustomerRevenue > 0)) {
      return {
        type: 'top_customer_current_month',
        text: `No seu escopo atual, o cliente com maior compra no mês corrente é ${topCustomerName}, com ${topCustomerDocuments} documentos e ${topCustomerRevenue.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA faturados.`,
      };
    }

    return {
      type: 'top_customer_unavailable',
      text: 'No seu escopo atual, não há dados suficientes para identificar o cliente com maior compra no mês corrente.',
    };
  }

  if (/metodo.*pagamento.*mais.*usado|forma.*pagamento.*mais.*usada|top.*metodo.*pagamento|qual.*pagamento.*lider/.test(normalized)) {
    const paymentMethodName = String(topPaymentMethodCurrentMonth?.name || '').trim();
    const paymentMethodTransactions = Number(topPaymentMethodCurrentMonth?.transactions || 0);
    const paymentMethodRevenue = Number(topPaymentMethodCurrentMonth?.revenue || 0);

    if (paymentMethodName && (paymentMethodTransactions > 0 || paymentMethodRevenue > 0)) {
      return {
        type: 'top_payment_method_current_month',
        text: `No seu escopo atual, o método de pagamento líder no mês corrente é ${paymentMethodName}, com ${paymentMethodTransactions} transações e ${paymentMethodRevenue.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA movimentados.`,
      };
    }

    return {
      type: 'top_payment_method_unavailable',
      text: 'No seu escopo atual, não há dados suficientes para identificar o método de pagamento líder no mês corrente.',
    };
  }

  if (/melhor loja|loja com maior receita|top loja|qual loja vende mais/.test(normalized)) {
    const topStore = Array.isArray(businessSummary?.topStores) ? businessSummary.topStores[0] : null;
    if (topStore?.store) {
      return {
        type: 'top_store',
        text: `No contexto disponível, a loja com melhor desempenho é ${topStore.store}, com receita de ${Number(topStore.revenue || 0).toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA.`,
      };
    }
  }

  if (/melhor periodo|maior receita|pior periodo|menor receita/.test(normalized) && trendStats) {
    return {
      type: 'best_worst_period',
      text: `No seu escopo atual, melhor período: ${formatTrendPoint(trendStats.best)}. Período mais fraco: ${formatTrendPoint(trendStats.worst)}.`,
    };
  }

  if (/transacoes|transacoes no periodo|numero de transacoes/.test(normalized) && trendStats) {
    const totalTransactions = (Array.isArray(businessSummary?.trend) ? businessSummary.trend : [])
      .reduce((acc, row) => acc + Number(row?.transactions || 0), 0);
    return {
      type: 'transactions_period',
      text: `No seu escopo atual, o total de transações no período analisado é ${totalTransactions}.`,
    };
  }

  if (/periodo analisado|qual periodo|intervalo|janela temporal/.test(normalized)) {
    const period = String(businessSummary?.period || 'month');
    const points = Number(trendStats?.points || 0);
    return {
      type: 'period_info',
      text: `No seu escopo atual, o período de referência é ${period}, com ${points} pontos de tendência carregados.`,
    };
  }

  if (/dados disponiveis|que dados tens|o que consegues responder|quais metricas/.test(normalized)) {
    return {
      type: 'available_metrics',
      text: 'Consigo responder com dados reais do seu escopo sobre: vendas, receita do mês, ticket médio, clientes, stock crítico, dívidas, equipa, meta mensal, tendência, períodos de melhor/pior receita e contagem de produtos (bens/serviços).',
    };
  }

  if (/faturacao hoje|receita hoje/.test(normalized)) {
    return {
      type: 'revenue_current_period',
      text: `No seu escopo atual, o valor de faturação no período ativo está em ${totalRevenue.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA.`,
    };
  }

  if (/ticket medio calculado|ticket pelo periodo|media por documento/.test(normalized)) {
    return {
      type: 'ticket_average_period',
      text: `No seu escopo atual, o ticket médio calculado no período é ${averageTicketMetric.toLocaleString('pt-AO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} AOA por documento.`,
    };
  }

  return null;
}

function resolveDeterministicContextIntent(scope, text) {
  const normalized = normalizeLabelKey(text);
  if (!normalized) return null;

  const normalizedScope = String(scope || 'global').toLowerCase();
  const scopedRules = Array.isArray(CONTEXT_INTENT_RULES?.[normalizedScope]) ? CONTEXT_INTENT_RULES[normalizedScope] : [];
  const globalRules = Array.isArray(CONTEXT_INTENT_RULES?.global) ? CONTEXT_INTENT_RULES.global : [];
  const sortedRules = [...scopedRules, ...globalRules]
    .slice()
    .sort((a, b) => Number(b?.priority || 0) - Number(a?.priority || 0));

  const matched = sortedRules.find((rule) => rule?.pattern instanceof RegExp && rule.pattern.test(normalized));
  const resolvedAnswer = typeof matched?.resolveAnswer === 'function'
    ? String(matched.resolveAnswer(text) || '').trim()
    : String(matched?.answer || '').trim();
  if (!resolvedAnswer) return null;

  const humanizedAnswer = humanizeContextIntentAnswer(
    normalizedScope,
    String(matched?.id || 'unknown_intent'),
    resolvedAnswer,
  );

  return {
    answer: humanizedAnswer,
    intentId: String(matched.id || 'unknown_intent'),
    priority: Number(matched.priority || 0),
    scope: normalizedScope,
    version: CONTEXT_INTENT_MAP_VERSION,
  };
}

function requestsOutOfScopeData(text) {
  const normalized = String(text || '').toLowerCase();
  if (!normalized) return false;

  return /outra empresa|outras empresas|empresa de outro|empresa do outro|concorrente|dados de outra|dados das outras|todas as empresas|outras lojas|loja de outro|dados de terceiros|cliente de outra empresa/i.test(normalized);
}

function normalizeNumeric(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeTrendSeries(data) {
  const trendSource = Array.isArray(data?.trendSeries)
    ? data.trendSeries
    : (Array.isArray(data?.salesChart?.weekly)
      ? data.salesChart.weekly
      : (Array.isArray(data?.salesChart?.hourly) ? data.salesChart.hourly : []));

  return trendSource
    .map((row, index) => ({
      label: String(row?.label || row?.period || row?.month || row?.dia || row?.date || `P${index + 1}`).trim(),
      revenue: normalizeNumeric(row?.revenue ?? row?.receita ?? row?.amount ?? row?.total ?? row?.sales ?? row?.valor),
      transactions: normalizeNumeric(row?.transactions ?? row?.transacoes ?? row?.count ?? row?.docs),
    }))
    .filter((row) => row.label)
    .slice(-6);
}

function summarizeBusinessSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return null;

  const kpis = Array.isArray(snapshot?.kpis)
    ? snapshot.kpis.slice(0, 8).map((kpi) => ({
      label: String(kpi?.label || '').trim(),
      value: String(kpi?.valueLabel || kpi?.value || '').trim(),
      change: normalizeNumeric(kpi?.change),
    })).filter((kpi) => kpi.label && kpi.value)
    : [];

  const trend = normalizeTrendSeries(snapshot);
  const topStores = Array.isArray(snapshot?.storePerformance)
    ? snapshot.storePerformance.slice(0, 5).map((row) => ({
      store: String(row?.store || row?.name || '').trim(),
      revenue: normalizeNumeric(row?.revenue ?? row?.receita),
      growth: normalizeNumeric(row?.growth ?? row?.crescimento),
    })).filter((row) => row.store)
    : [];

  const catalogMetrics = {
    totalProducts: normalizeNumeric(snapshot?.catalogMetrics?.totalProducts),
    goodsProducts: normalizeNumeric(snapshot?.catalogMetrics?.goodsProducts),
    serviceProducts: normalizeNumeric(snapshot?.catalogMetrics?.serviceProducts),
  };

  const moduleMetrics = {
    customers: {
      totalCustomers: normalizeNumeric(snapshot?.moduleMetrics?.customers?.totalCustomers),
      activeCustomers: normalizeNumeric(snapshot?.moduleMetrics?.customers?.activeCustomers),
      inactiveCustomers: normalizeNumeric(snapshot?.moduleMetrics?.customers?.inactiveCustomers),
      agtValidatedCustomers: normalizeNumeric(snapshot?.moduleMetrics?.customers?.agtValidatedCustomers),
      agtPendingCustomers: normalizeNumeric(snapshot?.moduleMetrics?.customers?.agtPendingCustomers),
      debtorCustomersCount: normalizeNumeric(snapshot?.moduleMetrics?.customers?.debtorCustomersCount),
      totalOpenBalance: normalizeNumeric(snapshot?.moduleMetrics?.customers?.totalOpenBalance),
      totalCredit: normalizeNumeric(snapshot?.moduleMetrics?.customers?.totalCredit),
      topDebtorName: String(snapshot?.moduleMetrics?.customers?.topDebtorName || '').trim(),
      topDebtorOpenBalance: normalizeNumeric(snapshot?.moduleMetrics?.customers?.topDebtorOpenBalance),
      topDebtors: Array.isArray(snapshot?.moduleMetrics?.customers?.topDebtors)
        ? snapshot.moduleMetrics.customers.topDebtors
          .map((row) => ({
            name: String(row?.name || '').trim(),
            openBalance: normalizeNumeric(row?.openBalance),
          }))
          .filter((row) => row.name)
          .slice(0, 3)
        : [],
    },
    suppliers: {
      totalSuppliers: normalizeNumeric(snapshot?.moduleMetrics?.suppliers?.totalSuppliers),
      activeSuppliers: normalizeNumeric(snapshot?.moduleMetrics?.suppliers?.activeSuppliers),
      averageRating: normalizeNumeric(snapshot?.moduleMetrics?.suppliers?.averageRating),
      totalContractsValue: normalizeNumeric(snapshot?.moduleMetrics?.suppliers?.totalContractsValue),
    },
    sales: {
      totalDocuments: normalizeNumeric(snapshot?.moduleMetrics?.sales?.totalDocuments),
      totalRevenue: normalizeNumeric(snapshot?.moduleMetrics?.sales?.totalRevenue),
      totalPaid: normalizeNumeric(snapshot?.moduleMetrics?.sales?.totalPaid),
      totalPending: normalizeNumeric(snapshot?.moduleMetrics?.sales?.totalPending),
      averageTicket: normalizeNumeric(snapshot?.moduleMetrics?.sales?.averageTicket),
      bestSalesDayCurrentMonth: {
        date: String(snapshot?.moduleMetrics?.sales?.bestSalesDayCurrentMonth?.date || '').trim(),
        label: String(snapshot?.moduleMetrics?.sales?.bestSalesDayCurrentMonth?.label || '').trim(),
        revenue: normalizeNumeric(snapshot?.moduleMetrics?.sales?.bestSalesDayCurrentMonth?.revenue),
        transactions: normalizeNumeric(snapshot?.moduleMetrics?.sales?.bestSalesDayCurrentMonth?.transactions),
      },
      worstSalesDayCurrentMonth: {
        date: String(snapshot?.moduleMetrics?.sales?.worstSalesDayCurrentMonth?.date || '').trim(),
        label: String(snapshot?.moduleMetrics?.sales?.worstSalesDayCurrentMonth?.label || '').trim(),
        revenue: normalizeNumeric(snapshot?.moduleMetrics?.sales?.worstSalesDayCurrentMonth?.revenue),
        transactions: normalizeNumeric(snapshot?.moduleMetrics?.sales?.worstSalesDayCurrentMonth?.transactions),
      },
      peakSalesHourCurrentMonth: {
        hour: normalizeNumeric(snapshot?.moduleMetrics?.sales?.peakSalesHourCurrentMonth?.hour),
        label: String(snapshot?.moduleMetrics?.sales?.peakSalesHourCurrentMonth?.label || '').trim(),
        revenue: normalizeNumeric(snapshot?.moduleMetrics?.sales?.peakSalesHourCurrentMonth?.revenue),
        transactions: normalizeNumeric(snapshot?.moduleMetrics?.sales?.peakSalesHourCurrentMonth?.transactions),
      },
      topProductCurrentMonth: {
        name: String(snapshot?.moduleMetrics?.sales?.topProductCurrentMonth?.name || '').trim(),
        quantity: normalizeNumeric(snapshot?.moduleMetrics?.sales?.topProductCurrentMonth?.quantity),
        revenue: normalizeNumeric(snapshot?.moduleMetrics?.sales?.topProductCurrentMonth?.revenue),
      },
      topProductsCurrentMonth: Array.isArray(snapshot?.moduleMetrics?.sales?.topProductsCurrentMonth)
        ? snapshot.moduleMetrics.sales.topProductsCurrentMonth
          .map((item) => ({
            name: String(item?.name || '').trim(),
            quantity: normalizeNumeric(item?.quantity),
            revenue: normalizeNumeric(item?.revenue),
          }))
          .filter((item) => item.name)
          .slice(0, 3)
        : [],
      topCustomerCurrentMonth: {
        name: String(snapshot?.moduleMetrics?.sales?.topCustomerCurrentMonth?.name || '').trim(),
        documents: normalizeNumeric(snapshot?.moduleMetrics?.sales?.topCustomerCurrentMonth?.documents),
        revenue: normalizeNumeric(snapshot?.moduleMetrics?.sales?.topCustomerCurrentMonth?.revenue),
      },
      topPaymentMethodCurrentMonth: {
        name: String(snapshot?.moduleMetrics?.sales?.topPaymentMethodCurrentMonth?.name || '').trim(),
        transactions: normalizeNumeric(snapshot?.moduleMetrics?.sales?.topPaymentMethodCurrentMonth?.transactions),
        revenue: normalizeNumeric(snapshot?.moduleMetrics?.sales?.topPaymentMethodCurrentMonth?.revenue),
      },
    },
    compliance: {
      softwareValidationNumber: String(snapshot?.moduleMetrics?.compliance?.softwareValidationNumber || '').trim(),
      certificationDate: String(snapshot?.moduleMetrics?.compliance?.certificationDate || '').trim(),
      certificationYear: String(snapshot?.moduleMetrics?.compliance?.certificationYear || '').trim(),
      softwareName: String(snapshot?.moduleMetrics?.compliance?.softwareName || '').trim(),
      softwareVersion: String(snapshot?.moduleMetrics?.compliance?.softwareVersion || '').trim(),
    },
  };

  return {
    generatedAt: snapshot?.generatedAt || new Date().toISOString(),
    period: String(snapshot?.period || '').trim() || 'month',
    kpis,
    trend,
    topStores,
    catalogMetrics,
    moduleMetrics,
  };
}

function maskString(value, keepStart = 3, keepEnd = 2) {
  const s = String(value || '');
  if (!s) return '';
  if (s.length <= keepStart + keepEnd + 2) return '***';
  return `${s.slice(0, keepStart)}...${s.slice(-keepEnd)}`;
}

function sanitizeBusinessSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return null;

  const safe = {};
  safe.generatedAt = snapshot.generatedAt || new Date().toISOString();
  safe.period = String(snapshot.period || '').slice(0, 64) || 'month';

  // KPIs: keep label and value only, truncate label
  safe.kpis = Array.isArray(snapshot.kpis)
    ? snapshot.kpis.slice(0, 8).map((k) => ({
      label: String(k?.label || '').slice(0, 60),
      value: String(k?.value || '').slice(0, 64),
      change: typeof k?.change === 'number' ? Number(k.change) : 0,
    })).filter((k) => k.label)
    : [];

  // Trend: keep label + numeric revenue/transactions, limit points
  safe.trend = Array.isArray(snapshot.trend)
    ? snapshot.trend.slice(-6).map((r) => ({
      label: String(r?.label || r?.period || '').slice(0, 40) || undefined,
      revenue: Number(r?.revenue || 0),
      transactions: Number(r?.transactions || 0),
    })).filter((r) => r.label)
    : [];

  // Top stores: mask store names and keep numeric metrics only
  safe.topStores = Array.isArray(snapshot.topStores)
    ? snapshot.topStores.slice(0, 5).map((s, idx) => ({
      store: maskString(String(s?.store || `Store${idx + 1}`)),
      revenue: Number(s?.revenue || 0),
      growth: Number(s?.growth || 0),
    }))
    : [];

  safe.catalogMetrics = {
    totalProducts: Number(snapshot?.catalogMetrics?.totalProducts || 0),
    goodsProducts: Number(snapshot?.catalogMetrics?.goodsProducts || 0),
    serviceProducts: Number(snapshot?.catalogMetrics?.serviceProducts || 0),
  };

  safe.moduleMetrics = {
    customers: {
      totalCustomers: Number(snapshot?.moduleMetrics?.customers?.totalCustomers || 0),
      activeCustomers: Number(snapshot?.moduleMetrics?.customers?.activeCustomers || 0),
      inactiveCustomers: Number(snapshot?.moduleMetrics?.customers?.inactiveCustomers || 0),
      agtValidatedCustomers: Number(snapshot?.moduleMetrics?.customers?.agtValidatedCustomers || 0),
      agtPendingCustomers: Number(snapshot?.moduleMetrics?.customers?.agtPendingCustomers || 0),
      debtorCustomersCount: Number(snapshot?.moduleMetrics?.customers?.debtorCustomersCount || 0),
      totalOpenBalance: Number(snapshot?.moduleMetrics?.customers?.totalOpenBalance || 0),
      totalCredit: Number(snapshot?.moduleMetrics?.customers?.totalCredit || 0),
      topDebtorName: String(snapshot?.moduleMetrics?.customers?.topDebtorName || '').trim(),
      topDebtorOpenBalance: Number(snapshot?.moduleMetrics?.customers?.topDebtorOpenBalance || 0),
      topDebtors: Array.isArray(snapshot?.moduleMetrics?.customers?.topDebtors)
        ? snapshot.moduleMetrics.customers.topDebtors
          .map((row, index) => ({
            name: maskString(String(row?.name || `Cliente${index + 1}`)),
            openBalance: Number(row?.openBalance || 0),
          }))
          .slice(0, 3)
        : [],
    },
    suppliers: {
      totalSuppliers: Number(snapshot?.moduleMetrics?.suppliers?.totalSuppliers || 0),
      activeSuppliers: Number(snapshot?.moduleMetrics?.suppliers?.activeSuppliers || 0),
      averageRating: Number(snapshot?.moduleMetrics?.suppliers?.averageRating || 0),
      totalContractsValue: Number(snapshot?.moduleMetrics?.suppliers?.totalContractsValue || 0),
    },
    sales: {
      totalDocuments: Number(snapshot?.moduleMetrics?.sales?.totalDocuments || 0),
      totalRevenue: Number(snapshot?.moduleMetrics?.sales?.totalRevenue || 0),
      totalPaid: Number(snapshot?.moduleMetrics?.sales?.totalPaid || 0),
      totalPending: Number(snapshot?.moduleMetrics?.sales?.totalPending || 0),
      averageTicket: Number(snapshot?.moduleMetrics?.sales?.averageTicket || 0),
      bestSalesDayCurrentMonth: {
        date: String(snapshot?.moduleMetrics?.sales?.bestSalesDayCurrentMonth?.date || '').slice(0, 32),
        label: String(snapshot?.moduleMetrics?.sales?.bestSalesDayCurrentMonth?.label || '').slice(0, 40),
        revenue: Number(snapshot?.moduleMetrics?.sales?.bestSalesDayCurrentMonth?.revenue || 0),
        transactions: Number(snapshot?.moduleMetrics?.sales?.bestSalesDayCurrentMonth?.transactions || 0),
      },
      worstSalesDayCurrentMonth: {
        date: String(snapshot?.moduleMetrics?.sales?.worstSalesDayCurrentMonth?.date || '').slice(0, 32),
        label: String(snapshot?.moduleMetrics?.sales?.worstSalesDayCurrentMonth?.label || '').slice(0, 40),
        revenue: Number(snapshot?.moduleMetrics?.sales?.worstSalesDayCurrentMonth?.revenue || 0),
        transactions: Number(snapshot?.moduleMetrics?.sales?.worstSalesDayCurrentMonth?.transactions || 0),
      },
      peakSalesHourCurrentMonth: {
        hour: Number(snapshot?.moduleMetrics?.sales?.peakSalesHourCurrentMonth?.hour || 0),
        label: String(snapshot?.moduleMetrics?.sales?.peakSalesHourCurrentMonth?.label || '').slice(0, 24),
        revenue: Number(snapshot?.moduleMetrics?.sales?.peakSalesHourCurrentMonth?.revenue || 0),
        transactions: Number(snapshot?.moduleMetrics?.sales?.peakSalesHourCurrentMonth?.transactions || 0),
      },
      topProductCurrentMonth: {
        name: maskString(String(snapshot?.moduleMetrics?.sales?.topProductCurrentMonth?.name || '')),
        quantity: Number(snapshot?.moduleMetrics?.sales?.topProductCurrentMonth?.quantity || 0),
        revenue: Number(snapshot?.moduleMetrics?.sales?.topProductCurrentMonth?.revenue || 0),
      },
      topProductsCurrentMonth: Array.isArray(snapshot?.moduleMetrics?.sales?.topProductsCurrentMonth)
        ? snapshot.moduleMetrics.sales.topProductsCurrentMonth
          .map((item, index) => ({
            name: maskString(String(item?.name || `Produto${index + 1}`)),
            quantity: Number(item?.quantity || 0),
            revenue: Number(item?.revenue || 0),
          }))
          .slice(0, 3)
        : [],
      topCustomerCurrentMonth: {
        name: maskString(String(snapshot?.moduleMetrics?.sales?.topCustomerCurrentMonth?.name || '')),
        documents: Number(snapshot?.moduleMetrics?.sales?.topCustomerCurrentMonth?.documents || 0),
        revenue: Number(snapshot?.moduleMetrics?.sales?.topCustomerCurrentMonth?.revenue || 0),
      },
      topPaymentMethodCurrentMonth: {
        name: maskString(String(snapshot?.moduleMetrics?.sales?.topPaymentMethodCurrentMonth?.name || '')),
        transactions: Number(snapshot?.moduleMetrics?.sales?.topPaymentMethodCurrentMonth?.transactions || 0),
        revenue: Number(snapshot?.moduleMetrics?.sales?.topPaymentMethodCurrentMonth?.revenue || 0),
      },
    },
    compliance: {
      softwareValidationNumber: maskString(String(snapshot?.moduleMetrics?.compliance?.softwareValidationNumber || '')),
      certificationDate: String(snapshot?.moduleMetrics?.compliance?.certificationDate || '').slice(0, 32),
      certificationYear: String(snapshot?.moduleMetrics?.compliance?.certificationYear || '').slice(0, 8),
      softwareName: maskString(String(snapshot?.moduleMetrics?.compliance?.softwareName || '')),
      softwareVersion: String(snapshot?.moduleMetrics?.compliance?.softwareVersion || '').slice(0, 24),
    },
  };

  // Scope: preserve roleValue but mask UUIDs
  safe.scope = {
    roleValue: String(snapshot?.scope?.roleValue || '').slice(0, 48),
    companyUuid: maskString(String(snapshot?.scope?.companyUuid || '')), 
    storeUuid: maskString(String(snapshot?.scope?.storeUuid || '')),
  };

  return safe;
}

function getHistoryStorageKey(userKey) {
  return `${CHAT_HISTORY_STORAGE_PREFIX}${String(userKey || 'anonymous').trim() || 'anonymous'}`;
}

function buildInitialMessages() {
  return [
    {
      role: 'assistant',
      content: INITIAL_ASSISTANT_GREETING,
      timestamp: new Date(),
    },
  ];
}

function normalizeHistoryMessages(items) {
  if (!Array.isArray(items)) return [];

  const normalized = items
    .map((item) => {
      const role = String(item?.role || '').trim().toLowerCase();
      const content = String(item?.content || '').trim();
      const timestampRaw = item?.timestamp;
      const timestamp = timestampRaw ? new Date(timestampRaw) : new Date();
      if (!['assistant', 'user'].includes(role) || !content) return null;

      return {
        role,
        content,
        timestamp: Number.isNaN(timestamp.getTime()) ? new Date() : timestamp,
      };
    })
    .filter(Boolean);

  if (!normalized.length) return [];

  return normalized.slice(-CHAT_HISTORY_LIMIT);
}

function loadPersistedHistory(userKey) {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(getHistoryStorageKey(userKey));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return normalizeHistoryMessages(parsed?.messages);
  } catch {
    return [];
  }
}

function persistHistory(userKey, messages) {
  if (typeof window === 'undefined') return;

  try {
    const normalized = normalizeHistoryMessages(messages);
    const payload = {
      version: 1,
      updatedAt: new Date().toISOString(),
      messages: normalized,
    };
    window.localStorage.setItem(getHistoryStorageKey(userKey), JSON.stringify(payload));
  } catch {
    // ignore local persistence errors
  }
}

function clearPersistedHistory(userKey) {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(getHistoryStorageKey(userKey));
  } catch {
    // ignore local persistence errors
  }
}

function resolveRouteContext(pathname) {
  const normalizedPath = String(pathname || '').replace(/\/+$/, '') || '/';
  const matched = PAGE_CONTEXT_RULES.find((rule) => rule.test(normalizedPath));

  if (!matched) {
    const pageName = titleFromPath(normalizedPath);
    return {
      pageName,
      context: `O utilizador esta na pagina "${pageName}" (rota ${normalizedPath}). Forneca orientacoes praticas, passo a passo, focadas nesta tela e no fluxo atual.`,
      suggestions: QUICK_SUGGESTIONS,
      scope: String(normalizedPath.split('/')[1] || 'global').toLowerCase(),
    };
  }

  return {
    pageName: matched.pageName,
    context: `${matched.context} Pagina atual: ${matched.pageName} (rota ${normalizedPath}).`,
    suggestions: matched.suggestions,
    scope: matched.scope || String(normalizedPath.split('/')[1] || 'global').toLowerCase(),
  };
}

function readLocalTelemetryEvents() {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(CHAT_TELEMETRY_LOCAL_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.events) ? parsed.events : [];
  } catch {
    return [];
  }
}

function appendLocalTelemetryEvent(eventPayload) {
  if (typeof window === 'undefined') return;

  try {
    const existing = readLocalTelemetryEvents();
    const next = [...existing, eventPayload].slice(-CHAT_TELEMETRY_LIMIT);
    window.localStorage.setItem(CHAT_TELEMETRY_LOCAL_KEY, JSON.stringify({ version: 1, events: next }));
  } catch {
    // ignore telemetry persistence errors
  }
}

function readTelemetryQueue() {
  if (typeof window === 'undefined') return [];

  try {
    const raw = window.localStorage.getItem(CHAT_TELEMETRY_QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed?.events) ? parsed.events : [];
  } catch {
    return [];
  }
}

function writeTelemetryQueue(events) {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(CHAT_TELEMETRY_QUEUE_KEY, JSON.stringify({ version: 1, events: events.slice(-CHAT_TELEMETRY_LIMIT) }));
  } catch {
    // ignore queue persistence errors
  }
}

export default function FloatingChatbot() {
  const location = useLocation();
  const [authSnapshot, setAuthSnapshot] = useState(readAuthSessionSnapshot);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [viewportWidth, setViewportWidth] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1024));
  const [viewportHeight, setViewportHeight] = useState(() => (typeof window !== 'undefined' ? window.innerHeight : 800));
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isLowSpecDevice, setIsLowSpecDevice] = useState(false);
  const [input, setInput] = useState('');
  const [isClearHistoryModalOpen, setIsClearHistoryModalOpen] = useState(false);
  const [messages, setMessages] = useState(buildInitialMessages);
  const [hasUnread, setHasUnread] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const deterministicHybridExpectedPrefixRef = useRef('');
  const deterministicHybridFallbackTextRef = useRef('');
  const telemetryInFlightRef = useRef(false);
  const historyHydratedRef = useRef(false);
  const sentMessageCountRef = useRef(0);
  const businessContextCacheRef = useRef({
    key: '',
    fetchedAt: 0,
    data: null,
    error: null,
  });

  const aiProvider = String(import.meta?.env?.VITE_AI_PROVIDER || 'TOGETHER').trim() || 'TOGETHER';
  const aiModel = String(import.meta?.env?.VITE_TOGETHER_MODEL || 'meta-llama/Llama-3.1-8B-Instruct-Turbo').trim() || 'meta-llama/Llama-3.1-8B-Instruct-Turbo';

  const { response, isLoading, error, sendMessage } = useChat(aiProvider, aiModel, true);

  const routeContext = resolveRouteContext(location?.pathname || '/');
  const userKey = String(authSnapshot?.userKey || 'anonymous').trim() || 'anonymous';

  const loadScopedBusinessContext = React.useCallback(async (questionText = '') => {
    const token = String(authSnapshot?.token || '').trim();
    const roleValue = String(authSnapshot?.roleValue || '').trim().toLowerCase();
    const companyUuid = String(authSnapshot?.companyUuid || '').trim();
    const storeUuid = String(authSnapshot?.storeUuid || '').trim();
    const cacheKey = `${userKey}|${companyUuid}|${storeUuid}|${roleValue}`;
    const now = Date.now();

    const shouldUseCache = BUSINESS_CONTEXT_CACHE_TTL_MS > 0
      && businessContextCacheRef.current.key === cacheKey
      && businessContextCacheRef.current.data
      && now - Number(businessContextCacheRef.current.fetchedAt || 0) < BUSINESS_CONTEXT_CACHE_TTL_MS;

    if (shouldUseCache) {
      return businessContextCacheRef.current.data;
    }

    if (!companyUuid || INVALID_BUSINESS_CONTEXT_TOKENS.has(token)) {
      return null;
    }

    const normalizedQuestion = String(questionText || '').trim();

    const prefersCeoScope = ['executive', 'gerente_geral', 'admin_super', 'admin'].includes(roleValue);
    const attempts = prefersCeoScope
      ? [
        () => apiGetAssistantBusinessContext(token, { period: 'month', months: 6, question: normalizedQuestion }),
        () => apiGetCeoDashboard(token, { companyUuid, storeUuid: undefined, period: 'month', consolidated: true }),
        () => apiGetStoreManagerDashboard(token, { companyUuid, storeUuid: storeUuid || undefined, period: 'month' }),
      ]
      : [
        () => apiGetAssistantBusinessContext(token, { period: 'month', months: 6, question: normalizedQuestion }),
        () => apiGetStoreManagerDashboard(token, { companyUuid, storeUuid: storeUuid || undefined, period: 'month' }),
        () => apiGetCeoDashboard(token, { companyUuid, storeUuid: undefined, period: 'month', consolidated: true }),
      ];

    let lastError = null;

    for (const attempt of attempts) {
      try {
        const data = await attempt();
        const summary = summarizeBusinessSnapshot(data);
        if (!summary) continue;

        const scopedSummary = {
          ...summary,
          scope: {
            companyUuid,
            storeUuid: storeUuid || null,
            roleValue,
          },
        };

        businessContextCacheRef.current = {
          key: cacheKey,
          fetchedAt: now,
          data: scopedSummary,
          error: null,
        };

        return scopedSummary;
      } catch (error) {
        lastError = error;
      }
    }

    const errorMessage = String(lastError?.message || '').trim() || 'business_context_unavailable';

    businessContextCacheRef.current = {
      key: cacheKey,
      fetchedAt: now,
      data: null,
      error: errorMessage,
    };

    return null;
  }, [authSnapshot?.companyUuid, authSnapshot?.roleValue, authSnapshot?.storeUuid, authSnapshot?.token, userKey]);

  const flushTelemetryQueue = React.useCallback(async () => {
    if (telemetryInFlightRef.current) return;

    const queuedEvents = readTelemetryQueue();
    if (!queuedEvents.length) return;

    telemetryInFlightRef.current = true;

    try {
      const remaining = [];

      for (const queued of queuedEvents) {
        const payload = queued?.payload;
        if (!payload || typeof payload !== 'object') continue;

        try {
          const tracked = await apiTrackObservabilityEvent(authSnapshot?.token || '', payload, { silent: true });
          if (!tracked) {
            remaining.push(queued);
          }
        } catch {
          remaining.push(queued);
        }
      }

      writeTelemetryQueue(remaining);
    } finally {
      telemetryInFlightRef.current = false;
    }
  }, [authSnapshot?.token]);

  const trackAssistantEvent = React.useCallback((eventName, details = {}) => {
    const now = new Date().toISOString();
    const payload = {
      module: 'virtual-assistant',
      event: String(eventName || 'assistant_event').trim() || 'assistant_event',
      severity: 'info',
      message: `chatbot:${String(eventName || 'assistant_event')}`,
      metadata: {
        ...details,
        user_key: userKey,
        user_name: authSnapshot?.userName || 'Utilizador',
        route: String(location?.pathname || '/'),
        context_scope: routeContext?.scope || 'global',
        created_at: now,
      },
    };

    appendLocalTelemetryEvent(payload);

    apiTrackObservabilityEvent(authSnapshot?.token || '', payload, { silent: true })
      .then((result) => {
        if (!result) {
          const queue = readTelemetryQueue();
          writeTelemetryQueue([...queue, { payload, queuedAt: now }]);
        }
      })
      .catch(() => {
        const queue = readTelemetryQueue();
        writeTelemetryQueue([...queue, { payload, queuedAt: now }]);
      });
  }, [authSnapshot?.token, authSnapshot?.userName, location?.pathname, routeContext?.scope, userKey]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleAuthRefresh = () => {
      setAuthSnapshot(readAuthSessionSnapshot());
    };

    handleAuthRefresh();
    window.addEventListener(ERP_AUTH_EVENT, handleAuthRefresh);
    window.addEventListener('storage', handleAuthRefresh);

    return () => {
      window.removeEventListener(ERP_AUTH_EVENT, handleAuthRefresh);
      window.removeEventListener('storage', handleAuthRefresh);
    };
  }, []);

  useEffect(() => {
    const persisted = loadPersistedHistory(userKey);

    if (persisted.length) {
      setMessages(persisted);
    } else {
      setMessages(buildInitialMessages());
    }

    historyHydratedRef.current = true;
    sentMessageCountRef.current = 0;
  }, [userKey]);

  useEffect(() => {
    if (!historyHydratedRef.current) return;
    persistHistory(userKey, messages);
  }, [messages, userKey]);

  useEffect(() => {
    flushTelemetryQueue();
  }, [flushTelemetryQueue]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handleOnline = () => {
      flushTelemetryQueue();
    };

    window.addEventListener('online', handleOnline);
    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [flushTelemetryQueue]);

  useEffect(() => {
    if (!error) return;

    deterministicHybridExpectedPrefixRef.current = '';
    deterministicHybridFallbackTextRef.current = '';

    console.error('Erro ao conectar com o assistente. Tente novamente.', error);
    const errorMessage = String(error?.message || '').trim();
    const isConfigMissing = errorMessage.includes('CONFIG_MISSING:');
    const assistantFeedback = isConfigMissing
      ? 'Assistente indisponivel: falta configurar a URL do servico de chat (VITE_AWS_LAMBDA_CHAT_COMPLETION_URL).'
      : 'Não consegui conectar ao assistente agora. Verifique a configuração do serviço e tente novamente.';

    trackAssistantEvent('response_error', {
      error_message: String(error?.message || 'unknown_error').slice(0, 240),
      sent_messages_count: sentMessageCountRef.current,
    });
    setMessages((prev) => {
      const filtered = prev.filter((msg) => !msg?.isStreaming);
      return [
        ...filtered,
        {
          role: 'assistant',
          content: assistantFeedback,
          timestamp: new Date(),
        },
      ];
    });
  }, [error, trackAssistantEvent]);

  useEffect(() => {
    if (response && !isLoading) {
      const expectedPrefix = String(deterministicHybridExpectedPrefixRef.current || '').trim();
      const fallbackDeterministic = String(deterministicHybridFallbackTextRef.current || '').trim();
      let finalResponse = String(response || '');

      if (expectedPrefix) {
        const normalizedResponse = finalResponse.trim().toLowerCase();
        const normalizedExpected = expectedPrefix.toLowerCase();

        if (!normalizedResponse.startsWith(normalizedExpected)) {
          finalResponse = fallbackDeterministic || expectedPrefix;
          trackAssistantEvent('business_hybrid_prefix_corrected', {
            used_fallback: true,
          });
        }
      }

      setMessages((prev) => {
        const last = prev?.[prev?.length - 1];
        if (last?.role === 'assistant' && last?.isStreaming) {
          return [
            ...prev?.slice(0, -1),
            { role: 'assistant', content: finalResponse, timestamp: new Date() },
          ];
        }
        return prev;
      });

      deterministicHybridExpectedPrefixRef.current = '';
      deterministicHybridFallbackTextRef.current = '';
      trackAssistantEvent('response_received', {
        response_chars: String(finalResponse || '').length,
      });
      if (!isOpen) setHasUnread(true);
    }
  }, [response, isLoading, isOpen, trackAssistantEvent]);

  useEffect(() => {
    if (isLoading && response) {
      setMessages((prev) => {
        const last = prev?.[prev?.length - 1];
        if (last?.isStreaming) {
          return [
            ...prev?.slice(0, -1),
            { role: 'assistant', content: response, timestamp: new Date(), isStreaming: true },
          ];
        }
        return prev;
      });
    }
  }, [response, isLoading]);

  useEffect(() => {
    messagesEndRef?.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (isOpen) {
      setHasUnread(false);
      setTimeout(() => inputRef?.current?.focus(), 300);
      trackAssistantEvent('chat_opened', {
        minimized: Boolean(isMinimized),
      });
    }
  }, [isMinimized, isOpen, trackAssistantEvent]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const updateViewport = () => {
      setViewportWidth(window.innerWidth);
      const vv = window.visualViewport;
      setViewportHeight(vv?.height || window.innerHeight);
    };

    updateViewport();

    const vv = window.visualViewport;
    window.addEventListener('resize', updateViewport);
    window.addEventListener('orientationchange', updateViewport);
    vv?.addEventListener('resize', updateViewport);
    vv?.addEventListener('scroll', updateViewport);

    return () => {
      window.removeEventListener('resize', updateViewport);
      window.removeEventListener('orientationchange', updateViewport);
      vv?.removeEventListener('resize', updateViewport);
      vv?.removeEventListener('scroll', updateViewport);
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handleMotionPreferenceChange = () => {
      setPrefersReducedMotion(Boolean(mediaQuery.matches));
    };

    handleMotionPreferenceChange();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleMotionPreferenceChange);
    } else if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleMotionPreferenceChange);
    }

    const hardwareThreads = Number(window.navigator?.hardwareConcurrency || 0);
    const deviceMemory = Number(window.navigator?.deviceMemory || 0);
    const lowSpecByCpu = hardwareThreads > 0 && hardwareThreads <= 4;
    const lowSpecByMemory = deviceMemory > 0 && deviceMemory <= 4;
    setIsLowSpecDevice(lowSpecByCpu || lowSpecByMemory);

    return () => {
      if (typeof mediaQuery.removeEventListener === 'function') {
        mediaQuery.removeEventListener('change', handleMotionPreferenceChange);
      } else if (typeof mediaQuery.removeListener === 'function') {
        mediaQuery.removeListener(handleMotionPreferenceChange);
      }
    };
  }, []);

  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    if (!(isOpen && !isMinimized && viewportWidth <= 768)) return undefined;

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMinimized, isOpen, viewportWidth]);

  useEffect(() => {
    if (!isClearHistoryModalOpen || typeof window === 'undefined') return undefined;

    const handleEscToCloseModal = (event) => {
      if (String(event?.key || '') === 'Escape') {
        setIsClearHistoryModalOpen(false);
      }
    };

    window.addEventListener('keydown', handleEscToCloseModal);
    return () => {
      window.removeEventListener('keydown', handleEscToCloseModal);
    };
  }, [isClearHistoryModalOpen]);

  const handleClearConversationHistory = React.useCallback(() => {
    if (isLoading) return;
    setIsClearHistoryModalOpen(true);
  }, [isLoading]);

  const handleConfirmClearConversationHistory = React.useCallback(() => {
    if (isLoading) return;

    clearPersistedHistory(userKey);
    deterministicHybridExpectedPrefixRef.current = '';
    deterministicHybridFallbackTextRef.current = '';
    businessContextCacheRef.current = {
      key: '',
      fetchedAt: 0,
      data: null,
      error: null,
    };
    sentMessageCountRef.current = 0;
    setInput('');
    setHasUnread(false);
    setMessages(buildInitialMessages());
    setIsClearHistoryModalOpen(false);

    trackAssistantEvent('conversation_history_cleared', {
      cleared_messages_count: Number(messages?.length || 0),
    });
  }, [isLoading, messages?.length, trackAssistantEvent, userKey]);

  const handleCancelClearConversationHistory = React.useCallback(() => {
    setIsClearHistoryModalOpen(false);
  }, []);

  // Early return AFTER all hooks
  const normalizedPath = String(location?.pathname || '').replace(/\/+$/, '') || '/';
  const isChatMobile = viewportWidth <= 768;
  const isChatVerySmallMobile = viewportWidth < 420;
  const reduceMotion = prefersReducedMotion || isLowSpecDevice;
  const isPosTerminalRoute = normalizedPath === '/pos-terminal' || normalizedPath.startsWith('/pos-terminal/');
  const hideOnPosTerminalMobile = isChatMobile && isPosTerminalRoute;
  const chatFloatingBottom = isChatMobile
    ? (isChatVerySmallMobile ? 'calc(env(safe-area-inset-bottom, 0px) + 8px)' : 'calc(env(safe-area-inset-bottom, 0px) + 12px)')
    : '24px';
  const chatFloatingRight = isChatMobile ? (isChatVerySmallMobile ? '8px' : '12px') : '24px';
  const chatPanelWidth = isChatMobile
    ? (isChatVerySmallMobile ? 'calc(100vw - 16px)' : 'calc(100vw - 24px)')
    : (isMinimized ? '320px' : '380px');
  const chatPanelHeight = isChatMobile
    ? (isMinimized ? 'auto' : `${Math.max(isChatVerySmallMobile ? 480 : 420, Math.floor(viewportHeight - (isChatVerySmallMobile ? 16 : 96)))}px`)
    : (isMinimized ? 'auto' : '560px');
  const chatMessagesMaxHeight = isChatMobile
    ? `${Math.max(isChatVerySmallMobile ? 300 : 240, Math.floor(viewportHeight - (isChatVerySmallMobile ? 240 : 280)))}px`
    : '380px';
  if (EXCLUDED_ROUTES?.includes(normalizedPath) || hideOnPosTerminalMobile) return null;

  const handleSend = async (text) => {
    const messageText = text || input?.trim();
    if (!messageText || isLoading) return;

    const userMessage = { role: 'user', content: messageText, timestamp: new Date() };

    if (requestsOutOfScopeData(messageText)) {
      setMessages((prev) => [
        ...prev,
        userMessage,
        {
          role: 'assistant',
          content: OUT_OF_SCOPE_POLICY_MESSAGE,
          timestamp: new Date(),
        },
      ]);
      setInput('');

      trackAssistantEvent('security_scope_blocked', {
        reason: 'out_of_scope_request',
        message_chars: messageText.length,
      });
      return;
    }

    const fiscalRuleFirstAnswer = buildFiscalRuleFirstAnswer(messageText);
    if (fiscalRuleFirstAnswer) {
      setMessages((prev) => [
        ...prev,
        userMessage,
        {
          role: 'assistant',
          content: fiscalRuleFirstAnswer,
          timestamp: new Date(),
        },
      ]);
      setInput('');

      trackAssistantEvent('fiscal_rule_first_answered', {
        context_scope: routeContext?.scope || 'global',
        message_chars: messageText.length,
      });
      return;
    }

    const deterministicContextResolution = resolveDeterministicContextIntent(routeContext?.scope || 'global', messageText);
    if (deterministicContextResolution?.answer) {
      setMessages((prev) => [
        ...prev,
        userMessage,
        {
          role: 'assistant',
          content: deterministicContextResolution.answer,
          timestamp: new Date(),
        },
      ]);
      setInput('');

      trackAssistantEvent('context_deterministic_answered', {
        context_scope: routeContext?.scope || 'global',
        context_intent_id: deterministicContextResolution.intentId,
        context_intent_priority: deterministicContextResolution.priority,
        context_intent_map_version: deterministicContextResolution.version,
        message_chars: messageText.length,
      });
      return;
    }

    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setInput('');

    setMessages((prev) => [
      ...prev,
      { role: 'assistant', content: '', timestamp: new Date(), isStreaming: true },
    ]);

    sentMessageCountRef.current += 1;

    const isAnalyticsQuestion = isBusinessDataQuestion(messageText);
    const scopeContextMessage = {
      role: 'system',
      content: `Escopo autorizado do utilizador: empresa_uuid=${authSnapshot?.companyUuid || 'indefinido'}; loja_uuid=${authSnapshot?.storeUuid || 'indefinido'}; role=${authSnapshot?.roleValue || 'indefinido'}. Nunca use dados fora deste escopo.`,
    };

    const businessSummary = await loadScopedBusinessContext(messageText);
    let businessContextMessage = null;
    let deterministicHybridInstructionMessage = null;
    let deterministicHybridAnswerType = null;

    if (businessSummary) {
      const safeSummary = sanitizeBusinessSnapshot(businessSummary);

      const deterministicAnswer = buildDeterministicBusinessAnswer(messageText, businessSummary);
      if (deterministicAnswer?.text) {
        const totalProducts = Number(businessSummary?.catalogMetrics?.totalProducts || 0);
        const goodsProducts = Number(businessSummary?.catalogMetrics?.goodsProducts || 0);
        const serviceProducts = Number(businessSummary?.catalogMetrics?.serviceProducts || 0);

        const lockedDeterministicText = String(deterministicAnswer.text || '').replace(/\s+/g, ' ').trim();
        deterministicHybridExpectedPrefixRef.current = lockedDeterministicText;
        deterministicHybridFallbackTextRef.current = lockedDeterministicText;
        deterministicHybridAnswerType = String(deterministicAnswer.type || 'unknown');

        if (STRICT_DETERMINISTIC_ANSWER_TYPES.has(deterministicHybridAnswerType)) {
          deterministicHybridExpectedPrefixRef.current = '';
          deterministicHybridFallbackTextRef.current = '';

          setMessages((prev) => {
            const filtered = prev.filter((msg) => !msg?.isStreaming);
            return [
              ...filtered,
              {
                role: 'assistant',
                content: lockedDeterministicText,
                timestamp: new Date(),
              },
            ];
          });

          trackAssistantEvent('business_deterministic_strict_answered', {
            answer_type: deterministicHybridAnswerType,
            deterministic_answer: true,
            hybrid_mode: false,
          });

          return;
        }

        deterministicHybridInstructionMessage = {
          role: 'system',
          content: `MODO HIBRIDO DETERMINISTICO: com base nos dados verificados, a sua resposta DEVE começar EXATAMENTE com este texto (sem alterar números, moeda ou unidades): "${lockedDeterministicText}". Depois desse texto inicial, adicione no máximo 2 frases curtas com interpretação prática e próxima ação recomendada no sistema. Não invente valores nem contradiga os dados verificados.`,
        };

        trackAssistantEvent('business_deterministic_answered', {
          total_products: Number.isFinite(totalProducts) ? totalProducts : -1,
          goods_products: Number.isFinite(goodsProducts) ? goodsProducts : -1,
          service_products: Number.isFinite(serviceProducts) ? serviceProducts : -1,
          answer_type: String(deterministicAnswer.type || 'unknown'),
          deterministic_answer: true,
          hybrid_mode: true,
        });
      }

      if (!deterministicAnswer?.text) {
        deterministicHybridExpectedPrefixRef.current = '';
        deterministicHybridFallbackTextRef.current = '';
      }

      businessContextMessage = {
        role: 'system',
        content: `Dados reais de negocio (campos sensiveis removidos/mascarados): ${JSON.stringify(safeSummary)}. Use estes dados para responder perguntas sobre vendas, tendencias, desempenho e catalogo de produtos. Se a pergunta for sobre quantidade de produtos, use catalogMetrics.totalProducts.`,
      };

      trackAssistantEvent('business_context_used', {
        trend_points: Number(businessSummary?.trend?.length || 0),
        kpi_count: Number(businessSummary?.kpis?.length || 0),
        analytics_question: isAnalyticsQuestion,
      });
    } else if (isAnalyticsQuestion) {
      deterministicHybridExpectedPrefixRef.current = '';
      deterministicHybridFallbackTextRef.current = '';
      const unavailableDataMessage = {
        role: 'assistant',
        content: 'Nao consegui carregar dados reais de negocio para esta analise no seu escopo agora. Para evitar informacao incorreta, nao vou estimar valores. Tente novamente em instantes ou consulte os painéis de KPI/Relatório Diário para confirmar os números atuais.',
        timestamp: new Date(),
      };

      setMessages((prev) => {
        const filtered = prev.filter((msg) => !msg?.isStreaming);
        return [...filtered, unavailableDataMessage];
      });
      trackAssistantEvent('business_context_unavailable', {
        analytics_question: true,
      });

      return;
    }

    if (!isAnalyticsQuestion) {
      deterministicHybridExpectedPrefixRef.current = '';
      deterministicHybridFallbackTextRef.current = '';
    }

    const pageContextMessage = {
      role: 'system',
      content: `Contexto da pagina atual: ${routeContext?.context || 'Sem contexto especifico.'}`,
    };

    const apiMessages = [
      SYSTEM_MESSAGE,
      scopeContextMessage,
      pageContextMessage,
      ...(businessContextMessage ? [businessContextMessage] : []),
      ...(deterministicHybridInstructionMessage ? [deterministicHybridInstructionMessage] : []),
      ...updatedMessages?.map((m) => ({ role: m?.role, content: m?.content })),
    ];

    trackAssistantEvent('message_sent', {
      message_chars: messageText.length,
      used_quick_suggestion: Boolean(text),
      context_scope: routeContext?.scope || 'global',
      conversation_size: updatedMessages.length,
      hybrid_mode: Boolean(deterministicHybridInstructionMessage),
      hybrid_answer_type: deterministicHybridAnswerType || null,
    });

    sendMessage(apiMessages, {
      max_completion_tokens: deterministicHybridInstructionMessage ? 220 : 512,
      ...(deterministicHybridInstructionMessage ? { temperature: 0.2 } : {}),
    });
  };

  const canClearHistory = messages.length > 1
    || String(messages?.[0]?.content || '').trim() !== INITIAL_ASSISTANT_GREETING;

  const handleKeyDown = (e) => {
    if (e?.key === 'Enter' && !e?.shiftKey) {
      e?.preventDefault();
      handleSend();
    }
  };

  const formatTime = (date) => {
    return new Date(date)?.toLocaleTimeString('pt-AO', { hour: '2-digit', minute: '2-digit' });
  };

  const chatUi = (
    <>
      {/* Floating Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.button
            initial={reduceMotion ? false : { scale: 0, opacity: 0 }}
            animate={reduceMotion ? { scale: 1, opacity: 1 } : { scale: 1, opacity: 1 }}
            exit={reduceMotion ? { opacity: 0 } : { scale: 0, opacity: 0 }}
            whileHover={reduceMotion ? undefined : { scale: 1.1 }}
            whileTap={reduceMotion ? undefined : { scale: 0.95 }}
            onClick={() => {
              setIsOpen(true);
              trackAssistantEvent('floating_button_click');
            }}
            className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center"
            style={{
              zIndex: 2200,
              bottom: chatFloatingBottom,
              right: chatFloatingRight,
              width: isChatMobile ? (isChatVerySmallMobile ? '50px' : '52px') : '56px',
              height: isChatMobile ? (isChatVerySmallMobile ? '50px' : '52px') : '56px',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              boxShadow: '0 0 30px rgba(99, 102, 241, 0.5)',
            }}
            aria-label="Abrir assistente IA"
          >
            <Bot className="w-6 h-6 text-white" />
            {hasUnread && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full border-2 border-white"
              />
            )}
            <motion.span
              animate={reduceMotion ? { opacity: 0.25, scale: 1 } : { scale: [1, 1.4, 1], opacity: [0.6, 0, 0.6] }}
              transition={reduceMotion ? { duration: 0 } : { duration: 2, repeat: Infinity }}
              className="absolute inset-0 rounded-full"
              style={{ background: 'rgba(99, 102, 241, 0.4)' }}
            />
          </motion.button>
        )}
      </AnimatePresence>
      {/* Chat Panel */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={reduceMotion ? false : { opacity: 0, scale: 0.85, y: 20, originX: 1, originY: 1 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.85, y: 20 }}
            transition={reduceMotion ? { duration: 0.12 } : { type: 'spring', stiffness: 300, damping: 30 }}
            className="fixed bottom-6 right-6 flex flex-col rounded-2xl overflow-hidden shadow-2xl"
            style={{
              zIndex: 2200,
              bottom: chatFloatingBottom,
              right: chatFloatingRight,
              width: chatPanelWidth,
              height: chatPanelHeight,
              maxWidth: isChatMobile ? (isChatVerySmallMobile ? 'calc(100vw - 16px)' : 'calc(100vw - 24px)') : '380px',
              maxHeight: isChatMobile ? (isChatVerySmallMobile ? 'calc(100dvh - 8px)' : 'calc(100dvh - 24px)') : '560px',
              borderRadius: isChatVerySmallMobile ? '16px' : '16px',
              background: 'rgba(10, 14, 26, 0.97)',
              border: '1px solid rgba(99, 102, 241, 0.3)',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 25px 60px rgba(0,0,0,0.6), 0 0 40px rgba(99, 102, 241, 0.15)',
            }}
          >
            {/* Header */}
            <div
              className="flex items-center justify-between px-4 py-3 flex-shrink-0"
              style={{
                background: 'linear-gradient(135deg, rgba(99,102,241,0.2) 0%, rgba(139,92,246,0.2) 100%)',
                borderBottom: '1px solid rgba(99, 102, 241, 0.2)',
              }}
            >
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
                >
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div>
                  <p className="text-white text-sm font-semibold leading-tight">Assistente IA</p>
                  <div className="flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
                      Online
                    </p>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleClearConversationHistory}
                  disabled={isLoading || !canClearHistory}
                  className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
                  aria-label="Limpar histórico da conversa"
                  title="Limpar histórico"
                >
                  <Trash2 className="w-3.5 h-3.5 text-white/60" />
                </button>
                {!isChatMobile && (
                  <button
                    onClick={() => {
                      const nextState = !isMinimized;
                      setIsMinimized(nextState);
                      trackAssistantEvent('chat_minimize_toggle', { minimized: nextState });
                    }}
                    className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:bg-white/10"
                    aria-label={isMinimized ? 'Expandir' : 'Minimizar'}
                  >
                    {isMinimized ? (
                      <Maximize2 className="w-3.5 h-3.5 text-white/60" />
                    ) : (
                      <Minimize2 className="w-3.5 h-3.5 text-white/60" />
                    )}
                  </button>
                )}
                <button
                  onClick={() => {
                    setIsOpen(false);
                    trackAssistantEvent('chat_closed');
                  }}
                  className="w-7 h-7 rounded-lg flex items-center justify-center transition-colors hover:bg-white/10"
                  aria-label="Fechar chat"
                >
                  <X className="w-3.5 h-3.5 text-white/60" />
                </button>
              </div>
            </div>

            {/* Body (hidden when minimized) */}
            <AnimatePresence>
              {!isMinimized && (
                <motion.div
                  initial={reduceMotion ? false : { height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={reduceMotion ? { opacity: 0 } : { height: 0, opacity: 0 }}
                  transition={reduceMotion ? { duration: 0.1 } : undefined}
                  className="flex flex-col flex-1 min-h-0"
                >
                  {/* Messages */}
                  <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0" style={{ maxHeight: chatMessagesMaxHeight }}>
                    {messages?.map((msg, i) => (
                      <motion.div
                        key={i}
                        initial={reduceMotion ? false : { opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={reduceMotion ? { duration: 0.08 } : { duration: 0.2 }}
                        className={`flex gap-2 ${msg?.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                      >
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{
                            background:
                              msg?.role === 'user' ?'linear-gradient(135deg, #10b981, #059669)' :'linear-gradient(135deg, #6366f1, #8b5cf6)',
                          }}
                        >
                          {msg?.role === 'user' ? (
                            <User className="w-3.5 h-3.5 text-white" />
                          ) : (
                            <Bot className="w-3.5 h-3.5 text-white" />
                          )}
                        </div>

                        <div className={`max-w-[75%] ${msg?.role === 'user' ? 'items-end' : 'items-start'} flex flex-col gap-1`}>
                          <div
                            className="px-3 py-2 rounded-2xl text-sm leading-relaxed"
                            style={{
                              background:
                                msg?.role === 'user' ?'linear-gradient(135deg, rgba(99,102,241,0.8), rgba(139,92,246,0.8))' :'rgba(255,255,255,0.07)',
                              color: 'rgba(255,255,255,0.9)',
                              borderRadius:
                                msg?.role === 'user' ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                              border: msg?.role === 'assistant' ? '1px solid rgba(255,255,255,0.08)' : 'none',
                            }}
                          >
                            {msg?.isStreaming && !msg?.content ? (
                              <div className="flex gap-1 items-center py-0.5">
                                <motion.span
                                  animate={reduceMotion ? { opacity: 0.7 } : { opacity: [0.3, 1, 0.3] }}
                                  transition={reduceMotion ? { duration: 0 } : { duration: 1, repeat: Infinity, delay: 0 }}
                                  className="w-1.5 h-1.5 rounded-full bg-indigo-400"
                                />
                                <motion.span
                                  animate={reduceMotion ? { opacity: 0.7 } : { opacity: [0.3, 1, 0.3] }}
                                  transition={reduceMotion ? { duration: 0 } : { duration: 1, repeat: Infinity, delay: 0.2 }}
                                  className="w-1.5 h-1.5 rounded-full bg-indigo-400"
                                />
                                <motion.span
                                  animate={reduceMotion ? { opacity: 0.7 } : { opacity: [0.3, 1, 0.3] }}
                                  transition={reduceMotion ? { duration: 0 } : { duration: 1, repeat: Infinity, delay: 0.4 }}
                                  className="w-1.5 h-1.5 rounded-full bg-indigo-400"
                                />
                              </div>
                            ) : (
                              msg?.content
                            )}
                          </div>
                          <span className="text-xs px-1" style={{ color: 'rgba(255,255,255,0.3)' }}>
                            {formatTime(msg?.timestamp)}
                          </span>
                        </div>
                      </motion.div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Quick Suggestions */}
                  {messages?.length === 1 && (
                    <div className="px-4 pb-2 flex flex-wrap gap-1.5">
                      {(routeContext?.suggestions || QUICK_SUGGESTIONS)?.map((s, i) => (
                        <motion.button
                          key={i}
                          initial={reduceMotion ? false : { opacity: 0, y: 4 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={reduceMotion ? { duration: 0.08 } : { delay: i * 0.05 }}
                          onClick={() => handleSend(s)}
                          className="text-xs px-2.5 py-1 rounded-full transition-all"
                          style={{
                            background: 'rgba(99,102,241,0.15)',
                            border: '1px solid rgba(99,102,241,0.3)',
                            color: 'rgba(255,255,255,0.7)',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(99,102,241,0.3)';
                            e.currentTarget.style.color = 'white';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(99,102,241,0.15)';
                            e.currentTarget.style.color = 'rgba(255,255,255,0.7)';
                          }}
                        >
                          {s}
                        </motion.button>
                      ))}
                    </div>
                  )}

                  {/* Input */}
                  <div
                    className="px-3 py-3 flex-shrink-0"
                    style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}
                  >
                    <div
                      className="flex items-end gap-2 rounded-xl px-3 py-2"
                      style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(99,102,241,0.25)',
                      }}
                    >
                      <textarea
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e?.target?.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Escreva uma mensagem..."
                        disabled={isLoading}
                        rows={1}
                        className="flex-1 bg-transparent text-sm resize-none outline-none leading-relaxed"
                        style={{
                          fontSize: isChatMobile ? '16px' : '14px',
                          color: 'rgba(255,255,255,0.9)',
                          maxHeight: '80px',
                          overflowY: 'auto',
                        }}
                        onInput={(e) => {
                          e.target.style.height = 'auto';
                          e.target.style.height = Math.min(e?.target?.scrollHeight, 80) + 'px';
                        }}
                      />
                      <motion.button
                        whileHover={reduceMotion ? undefined : { scale: 1.05 }}
                        whileTap={reduceMotion ? undefined : { scale: 0.95 }}
                        onClick={() => handleSend()}
                        disabled={!input?.trim() || isLoading}
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 transition-all"
                        style={{
                          background:
                            input?.trim() && !isLoading
                              ? 'linear-gradient(135deg, #6366f1, #8b5cf6)'
                              : 'rgba(255,255,255,0.08)',
                          cursor: input?.trim() && !isLoading ? 'pointer' : 'not-allowed',
                        }}
                        aria-label="Enviar mensagem"
                      >
                        {isLoading ? (
                          <Loader2 className="w-3.5 h-3.5 text-white/50 animate-spin" />
                        ) : (
                          <Send className="w-3.5 h-3.5 text-white" />
                        )}
                      </motion.button>
                    </div>
                    <p className="text-center text-xs mt-1.5" style={{ color: 'rgba(255,255,255,0.2)' }}>
                      Enter para enviar · Shift+Enter para nova linha
                    </p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {isClearHistoryModalOpen && (
                <motion.div
                  initial={reduceMotion ? false : { opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={reduceMotion ? { opacity: 0 } : { opacity: 0 }}
                  className="absolute inset-0 flex items-center justify-center px-4"
                  onClick={handleCancelClearConversationHistory}
                  style={{
                    background: 'rgba(4, 8, 16, 0.78)',
                    zIndex: 25,
                  }}
                >
                  <motion.div
                    initial={reduceMotion ? false : { opacity: 0, scale: 0.96, y: 6 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.96, y: 6 }}
                    transition={reduceMotion ? { duration: 0.1 } : { type: 'spring', stiffness: 280, damping: 24 }}
                    className="w-full max-w-[320px] rounded-xl p-4"
                    onClick={(event) => event.stopPropagation()}
                    style={{
                      background: 'rgba(14, 20, 36, 0.98)',
                      border: '1px solid rgba(99, 102, 241, 0.4)',
                      boxShadow: '0 18px 40px rgba(0,0,0,0.45)',
                    }}
                  >
                    <p className="text-sm font-semibold text-white">Limpar histórico?</p>
                    <p className="text-xs mt-2 leading-relaxed" style={{ color: 'rgba(255,255,255,0.72)' }}>
                      Esta ação remove toda a conversa atual e não pode ser desfeita.
                    </p>

                    <div className="mt-4 flex items-center justify-end gap-2">
                      <button
                        onClick={handleCancelClearConversationHistory}
                        className="px-3 py-1.5 text-xs rounded-lg"
                        style={{
                          background: 'rgba(255,255,255,0.08)',
                          border: '1px solid rgba(255,255,255,0.15)',
                          color: 'rgba(255,255,255,0.85)',
                        }}
                      >
                        Cancelar
                      </button>
                      <button
                        onClick={handleConfirmClearConversationHistory}
                        className="px-3 py-1.5 text-xs rounded-lg"
                        style={{
                          background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                          border: '1px solid rgba(239,68,68,0.6)',
                          color: 'white',
                        }}
                      >
                        Confirmar
                      </button>
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  if (typeof document === 'undefined' || !document.body) {
    return chatUi;
  }

  return createPortal(chatUi, document.body);
}
